import utils from "./utils.js";

// Core gallery: init, item collection, rendering, navigation, events, variation support.

const galleryMethods = {
    _init() {
        this._collectItems();
        this._initMobileLayoutObserver();
        this._buildDOM();
        if (this.opts.galleryType === "anchor" && !this._mobileLayoutActive) {
            this._bindStackedEvents();
        } else if (this.opts.galleryType === "sticky-split" && !this._mobileLayoutActive) {
            this._bindStickySplitEvents();
            if (this.opts.preloadImages) this._preloadAll();
        } else if (this.opts.galleryType === "hierarchy-grid" && !this._mobileLayoutActive) {
            this._bindHierarchyGridEvents();
            if (this.opts.preloadImages) this._preloadAll();
        } else if (this.opts.galleryType === "tiles" && !this._mobileLayoutActive) {
            this._bindTilesEvents();
            if (this.opts.preloadImages) this._preloadAll();
        } else {
            this._bindEvents();
            this._initMobileObserver();
            if (this.opts.preloadImages) this._preloadAll();
            requestAnimationFrame(() => this._updateFadeMask());
        }
        if (this.opts.onInit) this.opts.onInit.call(this);
    },

    _collectItems() {
        const nodes = this.wrapper.querySelectorAll(
            "[data-wcpg-image],[data-wcpg-video],[data-wcpg-embed]",
        );
        if (nodes.length) {
            nodes.forEach((el) => {
                const dataType = el.getAttribute("data-wcpg-type") || "";
                const hasEmbed = el.hasAttribute("data-wcpg-embed");
                const hasVid = el.hasAttribute("data-wcpg-video");

                let type, src, embed;

                if (
                    hasEmbed ||
                    dataType === "youtube" ||
                    dataType === "vimeo"
                ) {
                    // YouTube or Vimeo embed
                    type = dataType || "youtube";
                    embed = el.getAttribute("data-wcpg-embed") || "";
                    src = embed; // src === embed for display purposes
                } else if (hasVid || dataType === "video") {
                    // Hosted video file
                    type = "video";
                    src = el.getAttribute("data-wcpg-video") || "";
                    embed = "";
                } else {
                    // Standard image
                    type = "image";
                    src = el.getAttribute("data-wcpg-image") || "";
                    embed = "";
                }

                // ── Per-image linked video ────────────────────────────
                // Editors can set a video URL/file on each gallery image
                // via the WP media attachment panel. The PHP loader writes
                // data-wcpg-image-video-* attrs on standard image elements.
                const linkedVideoType =
                    type === "image"
                        ? el.getAttribute("data-wcpg-image-video-type") || ""
                        : "";
                const linkedVideoEmbed = linkedVideoType
                    ? el.getAttribute("data-wcpg-image-video-embed") || ""
                    : "";
                const linkedVideo = linkedVideoType
                    ? el.getAttribute("data-wcpg-image-video") || ""
                    : "";

                this.items.push({
                    src,
                    embed,
                    zoomSrc: el.getAttribute(this.opts.dataImageAttr) || src,
                    thumb:
                        el.getAttribute("data-wcpg-thumb") ||
                        (type === "image" ? src : ""),
                    caption:
                        el.getAttribute(this.opts.dataCaptionAttr) ||
                        el.getAttribute("alt") ||
                        "",
                    type,
                    poster: el.getAttribute("data-wcpg-poster") || "",
                    placeholder: el.getAttribute("data-wcpg-placeholder") || "",
                    w: parseInt(el.getAttribute("data-wcpg-w") || "0", 10),
                    h: parseInt(el.getAttribute("data-wcpg-h") || "0", 10),
                    // Linked video (may be empty strings when none set)
                    linkedVideoType,
                    linkedVideoEmbed,
                    linkedVideo,
                });
            });
        } else {
            const img = this.wrapper.querySelector("img");
            if (img) {
                this.items.push({
                    src: img.src,
                    zoomSrc:
                        img.getAttribute(this.opts.dataImageAttr) || img.src,
                    thumb: img.src,
                    caption: img.getAttribute("alt") || "",
                    type: "image",
                    poster: "",
                });
            }
        }
    },

    _renderItem(index, instant, dir = 0, isWrap = false) {
        const item = this.items[index];
        if (!item) return;
        const isVid = item.type === "video";
        const isEmbed = item.type === "youtube" || item.type === "vimeo";
        const isMedia = isVid || isEmbed; // any non-image item

        const swap = () => {
            // Snapshot container dimensions NOW — before hiding any element —
            // so the cover calculation gets the real image-driven height, not
            // the collapsed min-height that results after display:none is set.
            const snapW = this.mainDisplay.offsetWidth;
            const snapH = this.mainDisplay.offsetHeight;

            // ── Stop any active media before switching ─────────────────
            // Also clears any linked-video-active state from the previous item.
            this._stopLinkedVideo(
                true /* silent — iframe/video stop handled below */,
            );
            this.mainVideo.pause();
            if (this.mainIframe && this.mainIframe.style.display !== "none") {
                // Unload iframe to stop playback immediately
                this.mainIframe.src = "";
            }

            // Linked-video play button: always hidden for standalone media items.
            if (this._linkedVideoBtn)
                this._linkedVideoBtn.style.display = "none";

            if (isEmbed) {
                // ── YouTube / Vimeo embed ──────────────────────────────
                this.mainImg.style.display = "none";
                this.mainVideo.style.display = "none";
                this.mainVideo.classList.remove("wcpg-fit");
                this.mainVideo.src = "";
                if (this.mainIframe) {
                    if (this.opts.videoEmbedFitContainer) {
                        // Cover mode: size the iframe so the 16:9 video content
                        // fills the container in both axes (no black bars visible).
                        const cW = snapW;
                        const cH = snapH || Math.round((snapW * 9) / 16);
                        if (!this.mainDisplay.style.height) {
                            this.mainDisplay.style.height = cH + "px";
                        }
                        const videoRatio = 16 / 9;
                        const containerRatio = cW / cH;
                        let iW, iH;
                        if (containerRatio >= videoRatio) {
                            // Container wider than 16:9 — fill width, overflow height
                            iW = cW;
                            iH = Math.round(cW / videoRatio);
                        } else {
                            // Container taller than 16:9 — fill height, overflow width
                            iH = cH;
                            iW = Math.round(cH * videoRatio);
                        }
                        Object.assign(this.mainIframe.style, {
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: iW + "px",
                            height: iH + "px",
                            transform: "translate(-50%, -50%)",
                            aspectRatio: "unset",
                        });
                    } else {
                        this._resetIframeCoverStyles();
                    }
                    this.mainIframe.style.display = "block";
                    this.mainIframe.src = item.embed || item.src;
                }
                this.mainWrap.classList.add("wcpg-no-zoom");
                if (this.lens) this.lens.style.visibility = "hidden";
            } else if (isVid) {
                // ── Hosted video ───────────────────────────────────────
                this.mainImg.style.display = "none";
                if (this.mainIframe) {
                    this._resetIframeCoverStyles();
                    this.mainIframe.style.display = "none";
                    this.mainIframe.src = "";
                }
                // Apply fit-to-container: lock the display height then position
                // the video absolutely to fill it with object-fit:cover.
                if (this.opts.videoHostedFitContainer) {
                    if (!this.mainDisplay.style.height) {
                        this.mainDisplay.style.height = snapH + "px";
                    }
                    this.mainVideo.classList.add("wcpg-fit");
                } else {
                    this.mainVideo.classList.remove("wcpg-fit");
                }
                this.mainVideo.style.display = "block";
                this.mainVideo.src = item.src;
                this.mainVideo.poster = item.poster || "";
                this.mainVideo.muted = this.opts.videoHostedMuted;
                this.mainVideo.load();
                if (this.opts.videoHostedAutoplay) {
                    this.mainVideo.play().catch(() => {});
                }
                this.mainWrap.classList.add("wcpg-no-zoom");
                if (this.lens) this.lens.style.visibility = "hidden";
            } else {
                // ── Image ──────────────────────────────────────────────
                if (this.mainIframe) {
                    this._resetIframeCoverStyles();
                    this.mainIframe.style.display = "none";
                    this.mainIframe.src = "";
                }
                this.mainVideo.classList.remove("wcpg-fit");
                this.mainImg.style.display = "";
                this.mainVideo.style.display = "none";
                this.mainVideo.src = "";
                // Release the fit-container height set by video/embed when returning to images.
                // Fixed and Lock modes must keep their height intact.
                // Adaptive uses min-height (not height), so always clear explicit height.
                {
                    const _hm = this.opts.mainImageHeightMode;
                    if (_hm !== "fixed" && _hm !== "lock") {
                        this.mainDisplay.style.height = "";
                    }
                }
                // Adaptive: drive container height from each item's known dimensions so
                // every image gets its natural aspect ratio without the grow-only
                // min-height accumulating and bleeding into subsequent images.
                if (this.opts.mainImageHeightMode === "adaptive") {
                    this._adaptiveMaxH = null;
                    this.mainDisplay.style.minHeight = "";
                    this.mainDisplay.style.aspectRatio =
                        item.w && item.h ? `${item.w} / ${item.h}` : "";
                }
                // Show blurred placeholder while the full image loads (initial
                // page load only). this.placeholderImg is nulled by _hidePlaceholder
                // after first use, so this is a no-op for all subsequent navigations.
                if (
                    this.placeholderImg &&
                    item.placeholder &&
                    !(
                        this.mainImg.src === item.src &&
                        this.mainImg.complete &&
                        this.mainImg.naturalWidth > 0
                    )
                ) {
                    this._showPlaceholder(item);
                }
                this.mainImg.src = item.src;
                this.mainImg.alt = item.caption || "";
                // Block zoom until the image is rendered, unless the user has
                // opted in to allowing zoom before load (zoomBeforeLoad: true).
                // The 'load' handler lifts the block once naturalWidth/offsetHeight
                // are valid (needed for auto/match window sizing). For cached images
                // the browser sets complete=true synchronously after src assignment.
                if (!this.opts.zoomBeforeLoad) {
                    this.mainWrap.classList.add("wcpg-no-zoom");
                    if (this.lens) this.lens.style.visibility = "hidden";
                    if (
                        this.mainImg.complete &&
                        this.mainImg.naturalWidth > 0
                    ) {
                        this.mainWrap.classList.remove("wcpg-no-zoom");
                        if (this.lens) this.lens.style.visibility = "";
                    }
                }
                this._hideSpinner();

                // Show the linked-video play button when this image has a video.
                if (this._linkedVideoBtn) {
                    this._linkedVideoBtn.style.display = item.linkedVideoType
                        ? ""
                        : "none";
                }

                // Auto-play linked video when the relevant autoplay setting is on.
                if (item.linkedVideoType) {
                    const _lvIsEmbed =
                        item.linkedVideoType === "youtube" ||
                        item.linkedVideoType === "vimeo";
                    if (
                        (_lvIsEmbed && this.opts.videoAutoplay) ||
                        (!_lvIsEmbed && this.opts.videoHostedAutoplay)
                    ) {
                        this._playLinkedVideo();
                    }
                }
            }

            // ── Height mode: update after every swap ──────────────────────────
            // For adaptive: measure after layout and grow min-height if needed.
            // For lock with video/embed as first item: lock once layout settles.
            // Images in both modes are also handled by the 'load' event, which
            // covers slow-loading images; this handles cached images + all media.
            {
                const _hmSwap = this.opts.mainImageHeightMode;
                if (_hmSwap === "adaptive") {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() =>
                            this._updateAdaptiveHeight(),
                        );
                    });
                } else if (
                    _hmSwap === "lock" &&
                    !this.mainDisplay.style.height
                ) {
                    // Only before the first lock — no-op afterwards.
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => this._lockMainHeight());
                    });
                }
            }
        };

        if (instant) {
            swap();
            return;
        }

        const type = this.opts.mainImageTransition;
        const dur = this.opts.mainImageTransitionDuration;

        // Skip transitions when navigating to or from a video/embed item.
        // The media player has its own loading state; CSS transitions on the
        // container look wrong and can leave the display in a bad state.
        if (isMedia) {
            swap();
            return;
        }

        // ── Carousel slide — two images animate simultaneously ──────────
        if (type === "slide" && dir !== 0) {
            // Cancel any in-flight slide
            this._slideAnimKey = (this._slideAnimKey || 0) + 1;
            const key = this._slideAnimKey;
            this.mainDisplay
                .querySelectorAll(".wcpg-slide-out")
                .forEach((el) => el.remove());

            // Snapshot dimensions BEFORE swap() can cause any reflow.
            const snapContainerW = this.mainDisplay.offsetWidth;
            const snapContainerH = this.mainDisplay.offsetHeight;
            // Use actual element rect so outImg mirrors mainImg exactly,
            // regardless of object-fit mode or letterboxing.
            const displayRect = this.mainDisplay.getBoundingClientRect();
            const imgElemRect = this.mainImg.getBoundingClientRect();

            // 1. Capture the outgoing image as an absolute overlay
            const outImg = utils.el("img", "wcpg-slide-out");
            outImg.src = this.mainImg.src;
            outImg.alt = "";
            outImg.draggable = false;
            outImg.style.position = "absolute";
            outImg.style.left = imgElemRect.left - displayRect.left + "px";
            outImg.style.top = imgElemRect.top - displayRect.top + "px";
            outImg.style.width = imgElemRect.width + "px";
            outImg.style.height = imgElemRect.height + "px";
            outImg.style.objectFit = this.opts.mainImageFit;
            this.mainDisplay.appendChild(outImg);

            // Lock container height so swap() cannot reflow the layout —
            // without this, switching to a taller image (height:auto) would
            // expand the gallery area mid-animation, making the outgoing
            // small image look like it "stretched" to fill the space.
            this.mainDisplay.style.height = snapContainerH + "px";

            const _cleanupHeight = () => {
                // Release the animation-time height lock.
                // Fixed/Lock keep their own height set; adaptive uses min-height so
                // explicit height (set only during the slide animation) can be cleared.
                const _hm = this.opts.mainImageHeightMode;
                if (_hm !== "fixed" && _hm !== "lock") {
                    this.mainDisplay.style.height = "";
                }
            };

            const isVert = this.opts.mainImageSlideDirection === "vertical";
            const axis = isVert ? "translateY" : "translateX";

            if (isWrap) {
                // ── Wrap-around: short-range slide + opacity crossfade ─────
                const wrapDur = Math.round(dur * 0.65);
                const wrapEase = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";
                const wt = `transform ${wrapDur}ms ${wrapEase}, opacity ${wrapDur}ms ease`;
                const travel = Math.round(
                    (isVert ? snapContainerH : snapContainerW) * 0.3,
                );
                this.mainImg.style.transition = "none";
                this.mainImg.style.transform = `${axis}(${dir * travel}px)`;
                this.mainImg.style.opacity = "0";
                swap();
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (this._slideAnimKey !== key) return;
                        outImg.style.transition = wt;
                        this.mainImg.style.transition = wt;
                        outImg.style.transform = `${axis}(${-dir * travel}px)`;
                        outImg.style.opacity = "0";
                        this.mainImg.style.transform = `${axis}(0px)`;
                        this.mainImg.style.opacity = "1";
                        setTimeout(() => {
                            if (this._slideAnimKey !== key) return;
                            outImg.remove();
                            this.mainImg.style.transition = "";
                            this.mainImg.style.transform = "";
                            this.mainImg.style.opacity = "";
                            _cleanupHeight();
                        }, wrapDur + 20);
                    });
                });
                return;
            }

            // ── Normal slide: full-width/height pan ──────────────────────────────
            const travel = isVert ? snapContainerH : snapContainerW;
            this.mainImg.style.transition = "none";
            this.mainImg.style.transform = `${axis}(${dir * travel}px)`;

            swap();

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (this._slideAnimKey !== key) return;
                    const ease = "cubic-bezier(0.42, 0, 0.18, 1)";
                    const t = `transform ${dur}ms ${ease}`;
                    outImg.style.transition = t;
                    this.mainImg.style.transition = t;
                    outImg.style.transform = `${axis}(${-dir * travel}px)`;
                    this.mainImg.style.transform = `${axis}(0px)`;
                    setTimeout(() => {
                        if (this._slideAnimKey !== key) return;
                        outImg.remove();
                        this.mainImg.style.transition = "";
                        this.mainImg.style.transform = "";
                        _cleanupHeight();
                    }, dur + 20);
                });
            });
            return;
        }

        // ── Fade / Zoom / None ──────────────────────────────────────────
        this._showSpinner();

        this.mainDisplay.style.transition =
            `opacity ${dur}ms ease` +
            (type === "zoom" ? `, transform ${dur}ms ease` : "") +
            (type === "slide" ? `, transform ${dur}ms ease` : "");
        this.mainDisplay.style.opacity = "0";
        if (type === "zoom") this.mainDisplay.style.transform = "scale(0.94)";
        if (type === "slide")
            this.mainDisplay.style.transform = "translateX(12px)";

        setTimeout(
            () => {
                swap();
                requestAnimationFrame(() => {
                    this.mainDisplay.style.opacity = "1";
                    this.mainDisplay.style.transform = "";
                    setTimeout(() => {
                        this.mainDisplay.style.transition = "";
                    }, dur);
                });
            },
            type === "none" ? 0 : dur,
        );
    },

    _bindEvents() {
        if (this.opts.zoomOnHover) {
            utils.on(
                this.mainWrap,
                "mouseenter",
                (this._evZE = (e) => this._zoomEnter(e)),
            );
            utils.on(
                this.mainWrap,
                "mousemove",
                (this._evZM = (e) => this._zoomMove(e)),
            );
            utils.on(
                this.mainWrap,
                "mouseleave",
                (this._evZL = () => this._zoomLeave()),
            );
        }
        if (this.opts.touchZoom) {
            utils.on(
                this.mainWrap,
                "touchstart",
                (this._evTS = (e) => this._touchStart(e)),
                { passive: false },
            );
            utils.on(
                this.mainWrap,
                "touchmove",
                (this._evTM = (e) => this._touchMove(e)),
                { passive: false },
            );
            utils.on(
                this.mainWrap,
                "touchend",
                (this._evTE = (e) => this._touchEnd(e)),
            );
        }
        if (
            this.opts.mainImageSwipe &&
            !this.opts.zoomOnHover &&
            !this.opts.touchZoom &&
            this.items.length > 1 &&
            (this.opts.galleryType === "thumbnail" || this.opts.galleryType === "sticky-split")
        ) {
            this._bindMainSwipe(this.mainWrap);
        }
        if (
            this.opts.mainImageScrollNav &&
            this.items.length > 1 &&
            this.opts.galleryType === "thumbnail"
        ) {
            this._scrollNavAccum = 0;
            this._evMainScrollNav = (e) => {
                e.preventDefault();
                if (this._scrollNavTimer) return;
                // Normalise delta across deltaMode values (pixels / lines / pages)
                const raw = e.deltaMode === 1 ? e.deltaY * 30
                          : e.deltaMode === 2 ? e.deltaY * 300
                          : e.deltaY;
                this._scrollNavAccum += raw || e.deltaX;
                // Only fire once the accumulated scroll exceeds a meaningful threshold
                if (Math.abs(this._scrollNavAccum) < 50) return;
                const dir = this._scrollNavAccum > 0 ? 1 : -1;
                this._scrollNavAccum = 0;
                const delay = this.opts.mainImageScrollNavDelay ?? 500;
                this._scrollNavTimer = setTimeout(() => {
                    this._scrollNavTimer = null;
                    this._scrollNavAccum = 0;
                }, delay);
                this._goTo(this.index + dir);
            };
            this.mainWrap.addEventListener("wheel", this._evMainScrollNav, { passive: false });
        }
        if (this.lbTrigger) {
            utils.on(
                this.lbTrigger,
                "click",
                (this._evLbBtn = () => this._lbOpen(this.index)),
            );
        }
        if (this.mainPrev) {
            utils.on(
                this.mainPrev,
                "click",
                (this._evMP = (e) => {
                    e.stopPropagation();
                    e.currentTarget.blur();
                    this._goTo(this.index - 1);
                }),
            );
        }
        if (this.mainNext) {
            utils.on(
                this.mainNext,
                "click",
                (this._evMN = (e) => {
                    e.stopPropagation();
                    e.currentTarget.blur();
                    this._goTo(this.index + 1);
                }),
            );
        }
        if (this.pagination) {
            utils.on(
                this.pagination,
                "click",
                (this._evPG = (e) => {
                    const dot = e.target.closest(".wcpg-dot");
                    if (dot) { this._goTo(parseInt(dot.dataset.index)); return; }
                    if (e.target.closest(".wcpg-count-prev")) { this._goTo(this.index - 1); return; }
                    if (e.target.closest(".wcpg-count-next")) { this._goTo(this.index + 1); return; }
                }),
            );
        }

        // Disable zoom while hovering overlay controls (arrows, pagination, lb button).
        // These sit inside mainWrap so mouseleave/mouseenter on mainWrap won't re-fire
        // when moving between them — we must manually pause and resume here.
        // Note: pagination lives in stage (outside mainWrap) so it already gets
        // handled by mainWrap's mouseleave, but we include it for winCursor hiding.
        {
            const overlayEls = [
                this.lbTrigger,
                this.mainPrev,
                this.mainNext,
                this.pagination,
            ].filter(Boolean);
            this._overlayEls = overlayEls; // store for use in _goTo

            overlayEls.forEach((el) => {
                // Always call _zoomLeave (also hides winCursor) — even if isZooming
                // is momentarily false due to a recent _goTo reset.
                utils.on(el, "mouseenter", () => {
                    this._zoomLeave();
                    // Explicitly hide winCursor regardless of zoom state
                    if (this.winCursor) {
                        this.winCursor.classList.remove(
                            "wcpg-win-cursor-active",
                        );
                    }
                });
                utils.on(el, "mouseleave", (e) => {
                    // Resume only when moving back into the image area, not to another button.
                    const to = e.relatedTarget;
                    if (
                        this.opts.zoomOnHover &&
                        to &&
                        this.mainWrap.contains(to) &&
                        !overlayEls.some((b) => b.contains(to))
                    ) {
                        if (this._lastMouseEvent)
                            this._zoomEnter(this._lastMouseEvent);
                    }
                });
            });
        }
        // Open lightbox on main image click — desktop+touch
        // Enabled always on touch; on desktop also when lightboxOpenOnImageClick is true.
        // In click-trigger mode, image clicks on desktop belong to zoom — skip lightbox.
        if (this.opts.lightbox) {
            utils.on(
                this.mainWrap,
                "click",
                (this._evImgClick = (e) => {
                    if (e.target.closest(".wcpg-lb-trigger")) return;
                    if (e.target.closest(".wcpg-pagination")) return;
                    if (this._zs.pinching) return;
                    if (this._suppressClick) return;
                    if (this.opts.lightboxOpenOnImageClick) {
                        this._lbOpen(this.index);
                    }
                }),
            );
            if (this.opts.lightboxOpenOnImageClick && !this._isMobile) {
                this.mainWrap.style.cursor = "zoom-in";
            }
        }
        this._bindThumbListEvents();
        // Click on the video area to play/pause when native controls are hidden.
        // Stops propagation so lightbox does not open simultaneously.
        utils.on(this.mainVideo, "click", (this._evVidClick = (e) => {
            if (this.opts.videoControls) return;
            e.stopPropagation();
            if (this.mainVideo.paused) {
                this.mainVideo.play().catch(() => {});
            } else {
                this.mainVideo.pause();
            }
        }));
        utils.on(this.mainImg, "loadstart", () => this._showSpinner());
        utils.on(this.mainImg, "load", () => {
            this._hideSpinner();
            this._hidePlaceholder();
            if (!this.mainWrap.classList.contains("wcpg-linked-video-active")) {
                this.mainWrap.classList.remove("wcpg-no-zoom");
                if (this.lens) this.lens.style.visibility = "";
            }
            const _hm = this.opts.mainImageHeightMode;
            if (_hm === "lock") this._lockMainHeight();
            else if (_hm === "adaptive") this._updateAdaptiveHeight();
        });
        window.addEventListener("resize", this._resizeHandler);
        window.addEventListener("scroll", this._scrollHandler, {
            passive: true,
        });
        if (this.opts.stackedStickyEnabled) {
            this._initStackedSticky();
        }
    },

    _goTo(index) {
        // Stacked mode: update navigation indicator and scroll to item
        if (this.opts.galleryType === "anchor" && !this._mobileLayoutActive) {
            const len = this.items.length;
            if (!len) return;
            index = utils.clamp(index, 0, len - 1);
            if (index === this.index) return;
            this.index = index;
            if (this.opts.stackedNavigationType === "thumbs") {
                this._updateThumbActive(index);
                this._scrollThumbToIndex(index);
            } else {
                this._updatePagination(index);
            }
            this._scrollToStackedItem(index);
            return;
        }

        // Sticky Split mode: update main viewer + scroll scroll-col item into view
        if (this.opts.galleryType === "sticky-split" && !this._mobileLayoutActive) {
            const len = this.items.length;
            if (!len) return;
            index = utils.clamp(index, 0, len - 1);
            if (index === this.index) return;
            this.index = index;
            this._renderItem(index, true);
            this._scrollSSItemToView(index);
            this._updatePagination(index);
            return;
        }

        const len = this.items.length;
        if (!len) return;
        const prevIdx = this.index;
        if (this.opts.thumbnailLoop) {
            index = ((index % len) + len) % len;
        } else {
            index = utils.clamp(index, 0, len - 1);
        }
        if (index === prevIdx) return;
        this.index = index;

        // Detect wrap-around: going last→first or first→last
        const isWrap =
            (prevIdx === len - 1 && index === 0) ||
            (prevIdx === 0 && index === len - 1);

        // Keep directional slide for wrap — avoids the spinner that the fade path triggers.
        // The distinct wrap feel comes from a shorter-range translate + opacity crossfade
        // inside _renderItem when isWrap is true.
        let slideDir = isWrap
            ? prevIdx === len - 1 && index === 0
                ? 1
                : -1
            : index > prevIdx
            ? 1
            : -1;
        if (this._isRTL) slideDir = -slideDir;

        // Reset touch zoom
        if (this._zs.momentumRaf) {
            cancelAnimationFrame(this._zs.momentumRaf);
            this._zs.momentumRaf = null;
        }
        this._zoomLeave();
        this._zs.scale = 1;
        this._zs.panX = 0;
        this._zs.panY = 0;
        this._zs.originX = 0;
        this._zs.originY = 0;
        this.mainImg.style.transform = "";

        this._renderItem(index, false, slideDir, isWrap);
        this._updateThumbActive(index);
        this._scrollThumbToIndex(index, slideDir, isWrap);
        this._updatePagination(index);

        // Re-enter zoom if the cursor is still over the image area (mouseenter won't
        // re-fire because the pointer never left mainWrap). Skip if cursor is sitting
        // on an overlay control (arrow, pagination, lb trigger) — those pause zoom.
        if (
            this.opts.zoomOnHover &&
            this._lastMouseEvent &&
            this.mainWrap.matches(":hover")
        ) {
            const pt = document.elementFromPoint(
                this._lastMouseEvent.clientX,
                this._lastMouseEvent.clientY,
            );
            const overOverlay =
                pt &&
                this._overlayEls &&
                this._overlayEls.some((el) => el.contains(pt));
            if (!overOverlay) {
                const ev = this._lastMouseEvent;
                requestAnimationFrame(() => this._zoomEnter(ev));
            }
        }

        if (this.opts.onImageChange)
            this.opts.onImageChange.call(this, index, prevIdx);
        if (this.opts.onThumbClick) this.opts.onThumbClick.call(this, index);
    },

    _updateMainArrows(index) {
        if (!this.mainPrev || !this.mainNext) return;
        const len = this.items.length;
        const loop = this.opts.thumbnailLoop;
        this.mainPrev.classList.toggle(
            "wcpg-btn-disabled",
            !loop && index === 0,
        );
        this.mainNext.classList.toggle(
            "wcpg-btn-disabled",
            !loop && index === len - 1,
        );
    },

    _updatePagination(index) {
        if (!this.pagination) return;
        if (this.opts.mainImagePaginationType === "progressbar") {
            if (this.progressFill) {
                const pct =
                    this.items.length > 1
                        ? ((index + 1) / this.items.length) * 100
                        : 100;
                this.progressFill.style.width = pct + "%";
            }
            return;
        }
        if (this.opts.mainImagePaginationType === "count") {
            if (this._countText) {
                this._countText.textContent = `${index + 1} / ${this.items.length}`;
            }
            return;
        }
        if (!this.paginationDots || !this.paginationDots.length) return;
        this.paginationDots.forEach((dot, i) => {
            dot.classList.toggle("wcpg-dot-active", i === index);
        });
    },

    _showSpinner() {
        if (this.opts.spinner)
            this.spinner.classList.add("wcpg-spinner-active");
    },

    _hideSpinner() {
        this.spinner.classList.remove("wcpg-spinner-active");
    },

    _preloadAll() {
        this.items.forEach((item) => {
            if (item.type === "image") {
                new Image().src = item.src;
                if (item.zoomSrc && item.zoomSrc !== item.src)
                    new Image().src = item.zoomSrc;
            }
        });
    },

    _onResize() {
        this._clampThumbOffset();
        if (this.opts.zoomType === "window" && this.isZooming)
            this._positionZoomWindow();
        const _hm = this.opts.mainImageHeightMode;
        if (_hm === "lock") {
            this._relockMainHeight();
        } else if (_hm === "adaptive") {
            // Reset the tracked max so the natural height of the current item
            // (which may differ at the new container width) becomes the new baseline.
            this._adaptiveMaxH = null;
            this.mainDisplay.style.minHeight = "";
            requestAnimationFrame(() => {
                requestAnimationFrame(() => this._updateAdaptiveHeight());
            });
        }
    },


    _lockMainHeight() {
        const h = this.mainDisplay.offsetHeight;
        if (h > 0) this.mainDisplay.style.height = h + "px";
    },

    _relockMainHeight() {
        // When any media is active in the main display area (a direct YouTube/Vimeo
        // embed, a hosted video, or a linked video attached to an image), the main
        // image is hidden and the player element is either absolutely positioned or
        // sized by aspect-ratio. Clearing the locked height forces the browser to
        // reflow the container — but at fullscreen viewport dimensions the measured
        // height is wrong, so _lockMainHeight() then freezes the container at that
        // wrong value. YouTube native fullscreen fires window.resize both on enter
        // and on exit, which is what triggers this code path.
        // Fix: skip the relock entirely while any media is playing.
        const isMediaActive =
            // Linked video playing on top of an image item
            (this.mainWrap &&
                this.mainWrap.classList.contains("wcpg-linked-video-active")) ||
            // Direct YouTube / Vimeo embed gallery item
            (this.mainIframe &&
                this.mainIframe.style.display !== "none" &&
                this.mainIframe.src !== "") ||
            // Direct hosted-video gallery item
            (this.mainVideo &&
                this.mainVideo.style.display !== "none" &&
                this.mainVideo.src !== "");

        if (isMediaActive) return;

        // Temporarily release the lock so the browser can re-flow to the
        // natural height for the current width, then re-freeze.
        this.mainDisplay.style.height = "";
        requestAnimationFrame(() => {
            requestAnimationFrame(() => this._lockMainHeight());
        });
    },

    _updateAdaptiveHeight() {
        // When aspect-ratio is set (known item dimensions), the container sizes
        // itself naturally — no min-height accumulation needed.
        if (this.mainDisplay.style.aspectRatio) return;
        // Fallback for items without known dimensions: grow min-height to the
        // tallest measured height so far to avoid layout collapse.
        const h = this.mainDisplay.offsetHeight;
        if (h > 0 && h > (this._adaptiveMaxH || 0)) {
            this._adaptiveMaxH = h;
            this.mainDisplay.style.minHeight = h + "px";
        }
    },

    _refreshGalleryView() {
        this.index = 0;
        this._adaptiveMaxH = null;
        if (this.mainDisplay) this.mainDisplay.style.minHeight = "";

        const _showThumbs = this._mobileLayoutActive
            ? !!this.opts.mobileShowThumbs
            : this.opts.thumbnailShow !== false;
        const needThumbs = this.items.length > 1 && _showThumbs;

        if (this.thumbsList) {
            // Clear existing thumb DOM nodes
            this.thumbsList.innerHTML = "";
            this.thumbEls = [];

            // Reset slider offset — state AND CSS transform.
            // Without calling _applyThumbOffset the thumbsList element
            // retains whatever translateX/Y it had before the swap, making
            // the rebuilt thumbnails appear offset or invisible.
            if (this._ts) {
                this._ts.offset = 0;
                this._ts.loopAnimating = false;
                this._ts.scrollWrapAnimating = false;
            }
            this._applyThumbOffset(false);

            if (needThumbs) {
                // Ensure the strip is visible (may have been hidden during a previous reset)
                if (this.thumbsWrap) this.thumbsWrap.style.display = "";
                // Rebuild thumbnail elements for the new item set
                this._buildThumbItems();
            } else {
                // No items to show — hide the thumbnail strip
                if (this.thumbsWrap) this.thumbsWrap.style.display = "none";
            }

            // Defer button visibility + fade mask update until layout is settled
            requestAnimationFrame(() => {
                this._updateThumbButtons();
                this._updateFadeMask();
            });
        } else if (needThumbs) {
            // Thumbnails were not built during init (product had 0 or 1 images).
            // Build them now so the variation gallery is fully navigable.
            this._buildThumbnails();
            this._insertThumbsIntoLayout();
            this._bindThumbListEvents();
            this._setupFitObserver();
            this._initStackObserver();
            requestAnimationFrame(() => {
                this._updateThumbButtons();
                this._updateFadeMask();
            });
        }

        // Rebuild pagination to match the new item count.
        if (this.pagination) {
            if (this.opts.mainImagePaginationType === "progressbar" || this.opts.mainImagePaginationType === "count") {
                // Progress bar and count just need to be reset to index 0.
                this._updatePagination(0);
            } else {
                // Rebuild dot buttons inside the existing container so the
                // delegated click listener (bound in _bindEvents) keeps working.
                this.pagination.innerHTML = "";
                this.paginationDots = [];
                this.items.forEach((_, i) => {
                    const dot = utils.el(
                        "button",
                        "wcpg-dot" + (i === 0 ? " wcpg-dot-active" : ""),
                    );
                    dot.setAttribute("aria-label", `Go to image ${i + 1}`);
                    dot.setAttribute("data-index", i);
                    this.pagination.appendChild(dot);
                    this.paginationDots.push(dot);
                });
            }
        }

        // Render the first item and mark its thumbnail active
        this._renderItem(0, true);
        this._updateThumbActive(0);
    },

    // Bind delegated click/hover/keyboard and drag/scroll events to the thumbnail
    // strip. Extracted so both _bindEvents() and the lazy-build path in
    // _refreshGalleryView() can wire the same handlers without duplication.
    _bindThumbListEvents() {
        if (this.thumbsList) {
            utils.on(
                this.thumbsList,
                "click",
                (this._evTC = (e) => {
                    const t = e.target.closest(".wcpg-thumb-item");
                    if (t) this._goTo(parseInt(t.dataset.index));
                }),
            );
            if (this.opts.thumbnailTrigger === "hover") {
                utils.on(
                    this.thumbsList,
                    "mouseover",
                    (this._evTH = (e) => {
                        const t = e.target.closest(".wcpg-thumb-item");
                        if (t) this._goTo(parseInt(t.dataset.index));
                    }),
                );
            }
            utils.on(
                this.thumbsList,
                "keydown",
                (this._evTK = (e) => {
                    const t = e.target.closest(".wcpg-thumb-item");
                    if (!t) return;
                    const i = parseInt(t.dataset.index);
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        this._goTo(i);
                    }
                    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                        e.preventDefault();
                        this._goTo(
                            e.key === "ArrowRight" && this._isRTL
                                ? this.index - 1
                                : this.index + 1,
                        );
                    }
                    if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                        e.preventDefault();
                        this._goTo(
                            e.key === "ArrowLeft" && this._isRTL
                                ? this.index + 1
                                : this.index - 1,
                        );
                    }
                }),
            );
        }
        if (this.thumbPrev)
            utils.on(
                this.thumbPrev,
                "click",
                (this._evTP = () => {
                    if (!this.thumbPrev.classList.contains("wcpg-btn-disabled"))
                        this._slideThumb(-1);
                }),
            );
        if (this.thumbNext)
            utils.on(
                this.thumbNext,
                "click",
                (this._evTN = () => {
                    if (!this.thumbNext.classList.contains("wcpg-btn-disabled"))
                        this._slideThumb(1);
                }),
            );
        if (this.thumbsTrack && this.opts.thumbnailDragNav) this._bindThumbDrag();
        if (this.thumbsTrack && this.opts.thumbnailScrollNav) this._bindThumbScroll();
    },

    // Insert a newly-built thumbsWrap into the gallery layout at the correct
    // position. Called only when thumbnails are created lazily (product had 0 or
    // 1 images at init time, so _buildThumbnails() was skipped in _buildDOM()).
    _insertThumbsIntoLayout() {
        if (!this.thumbsWrap) return;

        const _effThumbPos = this._mobileLayoutActive
            ? (this.opts.mobileThumbnailPosition || "bottom")
            : this.opts.thumbnailPosition;
        const isHoriz = ["left", "right"].includes(_effThumbPos);

        // Apply mobile-specific size overrides now that the wrap exists
        if (this._mobileLayoutActive) {
            const s = this.wrapper.style;
            const mW = this.opts.mobileThumbnailWidth;
            const mH = this.opts.mobileThumbnailHeight;
            if (mW > 0) s.setProperty("--wcpg-thumb-w", mW + "px");
            if (mH > 0) s.setProperty("--wcpg-thumb-h", mH + "px");
        }

        if (isHoriz) {
            let row = this.wrapper.querySelector(".wcpg-row");
            if (!row) {
                row = utils.el("div", "wcpg-row");
                // Move stage out of the wrapper into the new row
                if (this.stage && this.stage.parentNode === this.wrapper) {
                    this.wrapper.removeChild(this.stage);
                }
                if (_effThumbPos === "left") {
                    row.appendChild(this.thumbsWrap);
                    if (this.stage) row.appendChild(this.stage);
                } else {
                    if (this.stage) row.appendChild(this.stage);
                    row.appendChild(this.thumbsWrap);
                }
                this.wrapper.appendChild(row);
            } else {
                if (_effThumbPos === "left") {
                    row.insertBefore(this.thumbsWrap, row.firstChild);
                } else {
                    row.appendChild(this.thumbsWrap);
                }
            }
            this.wrapper.classList.remove("wcpg-thumb-left", "wcpg-thumb-right", "wcpg-thumb-top", "wcpg-thumb-bottom");
            this.wrapper.classList.add(`wcpg-thumb-${_effThumbPos}`);
        } else {
            // Top / bottom: thumbsWrap sits directly in the wrapper after stage
            this.wrapper.appendChild(this.thumbsWrap);
        }
    },

    _bindStackedEvents() {
        if (this.pagination) {
            utils.on(
                this.pagination,
                "click",
                (this._evStackedPG = (e) => {
                    const dot = e.target.closest(".wcpg-dot");
                    if (dot)
                        this._scrollToStackedItem(parseInt(dot.dataset.index));
                }),
            );
        }
        if (this.opts.lightbox) {
            this._evStackedClicks = [];

            // Expand-icon buttons on individual image items
            if (this._stackedLbTriggers && this._stackedLbTriggers.length) {
                this._stackedLbTriggers.forEach(({ el, index }) => {
                    const fn = (e) => {
                        e.stopPropagation();
                        this._lbOpen(index);
                    };
                    utils.on(el, "click", fn);
                    this._evStackedClicks.push({ el, fn });
                });
            }

            // Click-to-open on image items (only when lightboxOpenOnImageClick is on)
            if (this.opts.lightboxOpenOnImageClick && this.stackedItems) {
                this.stackedItems.forEach((itemWrap, i) => {
                    const item = this.items[i];
                    if (!item || item.type !== "image") return;
                    const fn = (e) => {
                        if (e.target.closest(".wcpg-lb-trigger")) return;
                        this._lbOpen(i);
                    };
                    utils.on(itemWrap, "click", fn);
                    this._evStackedClicks.push({ el: itemWrap, fn });
                    if (!this._isMobile) itemWrap.style.cursor = "zoom-in";
                });
            }
        }
        // ── Thumbnail navigation events (thumbs mode only) ───────────
        if (this.opts.stackedNavigationType === "thumbs") {
            if (this.thumbsList) {
                utils.on(
                    this.thumbsList,
                    "click",
                    (this._evTC = (e) => {
                        const t = e.target.closest(".wcpg-thumb-item");
                        if (t) this._goTo(parseInt(t.dataset.index));
                    }),
                );
                if (this.opts.thumbnailTrigger === "hover") {
                    utils.on(
                        this.thumbsList,
                        "mouseover",
                        (this._evTH = (e) => {
                            const t = e.target.closest(".wcpg-thumb-item");
                            if (t) this._goTo(parseInt(t.dataset.index));
                        }),
                    );
                }
                utils.on(
                    this.thumbsList,
                    "keydown",
                    (this._evTK = (e) => {
                        const t = e.target.closest(".wcpg-thumb-item");
                        if (!t) return;
                        const i = parseInt(t.dataset.index);
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            this._goTo(i);
                        }
                        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                            e.preventDefault();
                            this._goTo(this.index + 1);
                        }
                        if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                            e.preventDefault();
                            this._goTo(this.index - 1);
                        }
                    }),
                );
            }
            if (this.thumbPrev) {
                utils.on(
                    this.thumbPrev,
                    "click",
                    (this._evTP = () => {
                        if (
                            !this.thumbPrev.classList.contains(
                                "wcpg-btn-disabled",
                            )
                        )
                            this._slideThumb(-1);
                    }),
                );
            }
            if (this.thumbNext) {
                utils.on(
                    this.thumbNext,
                    "click",
                    (this._evTN = () => {
                        if (
                            !this.thumbNext.classList.contains(
                                "wcpg-btn-disabled",
                            )
                        )
                            this._slideThumb(1);
                    }),
                );
            }
            if (this.thumbsTrack && !this._thumbDragBound) {
                if (this.opts.thumbnailDragNav) this._bindThumbDrag();
                if (this.opts.thumbnailScrollNav) this._bindThumbScroll();
                this._thumbDragBound = true;
            }
        }

        // Linked video play / close buttons
        this._stackedLvEvents = [];
        if (this.stackedItems) {
            this.stackedItems.forEach((itemWrap, i) => {
                const item = this.items[i];
                if (!item || !item.linkedVideoType) return;
                const playBtn = itemWrap.querySelector(
                    ".wcpg-linked-video-btn",
                );
                if (playBtn) {
                    const fn = (e) => {
                        e.stopPropagation();
                        this._playStackedLinkedVideo(itemWrap, item);
                    };
                    utils.on(playBtn, "click", fn);
                    this._stackedLvEvents.push({ el: playBtn, fn });
                }
                const closeBtn = itemWrap.querySelector(
                    ".wcpg-stacked-linked-close",
                );
                if (closeBtn) {
                    const fn = (e) => {
                        e.stopPropagation();
                        this._stopStackedLinkedVideo(itemWrap, item);
                    };
                    utils.on(closeBtn, "click", fn);
                    this._stackedLvEvents.push({ el: closeBtn, fn });
                }
            });
        }
        // Click-to-play/pause for hosted video items and linked hosted videos
        if (!this.opts.videoControls && this.stackedItems) {
            this.stackedItems.forEach((itemWrap, i) => {
                const item = this.items[i];
                if (!item) return;
                const vids = [];
                if (item.type === "video") {
                    const v = itemWrap.querySelector(".wcpg-stacked-video");
                    if (v) vids.push(v);
                }
                if (
                    item.linkedVideoType &&
                    item.linkedVideoType !== "youtube" &&
                    item.linkedVideoType !== "vimeo"
                ) {
                    const v = itemWrap.querySelector(".wcpg-stacked-lv-video");
                    if (v) vids.push(v);
                }
                vids.forEach((vid) => {
                    const fn = (e) => {
                        e.stopPropagation();
                        if (vid.paused) vid.play().catch(() => {});
                        else vid.pause();
                    };
                    utils.on(vid, "click", fn);
                    this._stackedLvEvents.push({ el: vid, fn });
                });
            });
        }
        if (this.opts.anchorZoom !== false && this.opts.zoomOnHover && !this._isMobile) {
            this._stackedZoomEvents = [];
            this.stackedItems &&
                this.stackedItems.forEach((itemWrap, i) => {
                    const item = this.items[i];
                    if (!item || item.type !== "image") return;
                    const img = itemWrap.querySelector(".wcpg-stacked-img");
                    if (!img) return;
                    const zoomDisplay =
                        itemWrap.querySelector(".wcpg-stacked-display") ||
                        itemWrap;
                    const enterFn = (e) => {
                        // Don't zoom while a linked video is playing in this item
                        if (
                            itemWrap.classList.contains(
                                "wcpg-stacked-lv-active",
                            )
                        )
                            return;
                        this.mainImg = img;
                        this.mainWrap = itemWrap;
                        this.mainDisplay = zoomDisplay;
                        if (this.index !== i) {
                            this.index = i;
                            // Keep dots/thumbs in sync so the scroll handler's
                            // activeIdx !== this.index comparison stays valid.
                            if (this.opts.stackedNavigationType === "thumbs") {
                                this._updateThumbActive(i);
                            } else {
                                this._updatePagination(i);
                            }
                        }
                        if (this.lens) zoomDisplay.appendChild(this.lens);
                        if (this.winCursor)
                            zoomDisplay.appendChild(this.winCursor);
                        this._zoomEnter(e);
                    };
                    const moveFn = (e) => this._zoomMove(e);
                    const leaveFn = () => {
                        this._zoomLeave();
                        setTimeout(() => {
                            if (!this.isZooming) {
                                this.mainImg = null;
                                this.mainWrap = null;
                                this.mainDisplay = null;
                                this._updateStackedActive();
                            }
                        }, 300);
                    };
                    utils.on(itemWrap, "mouseenter", enterFn);
                    utils.on(itemWrap, "mousemove", moveFn);
                    utils.on(itemWrap, "mouseleave", leaveFn);
                    this._stackedZoomEvents.push(
                        { el: itemWrap, event: "mouseenter", fn: enterFn },
                        { el: itemWrap, event: "mousemove", fn: moveFn },
                        { el: itemWrap, event: "mouseleave", fn: leaveFn },
                    );
                });
        }
        this._stackedScrollFn = () => this._updateStackedActive();
        window.addEventListener("scroll", this._stackedScrollFn, {
            passive: true,
        });
        window.addEventListener("resize", this._stackedScrollFn, {
            passive: true,
        });
        // Only recalculate on load when the browser has already restored a
        // non-zero scroll position — dot 0 is already marked active by the builder.
        if (window.scrollY > 0) {
            requestAnimationFrame(() => this._updateStackedActive());
        }
        // Trigger initial autoplay for item 0 (always active on a fresh page load).
        // If scrollY > 0, _updateStackedActive() above will correct to the real active
        // item and call _syncStackedVideo again to pause item 0 if needed.
        this._syncStackedVideo(-1, 0);

        // Sticky product summary — only for stacked layout
        if (this.opts.stackedStickyEnabled) {
            this._initStackedSticky();
        }

        // JS sticky fallbacks for dots rail and thumb sidebar.
        // Deferred one frame so the browser has committed the stacked layout
        // and offsetWidth / getBoundingClientRect return live values.
        requestAnimationFrame(() => {
            this._initAnchorDotStickyFallback();
            this._initStackedThumbStickyFallback();
        });
    },

    _updateStackedActive() {
        if (!this.stackedItems || !this.stackedItems.length) return;
        const mid = window.innerHeight * 0.5;
        let activeIdx = 0;
        for (let i = 0; i < this.stackedItems.length; i++) {
            const rect = this.stackedItems[i].getBoundingClientRect();
            if (rect.top <= mid) activeIdx = i;
        }
        if (activeIdx !== this.index) {
            const prevIdx = this.index;
            this.index = activeIdx;
            if (this.opts.stackedNavigationType === "thumbs") {
                this._updateThumbActive(activeIdx);
                this._scrollThumbToIndex(activeIdx);
            } else {
                this._updatePagination(activeIdx);
            }
            this._syncStackedVideo(prevIdx, activeIdx);
        }
    },

    _playStackedLinkedVideo(itemWrap, item) {
        const isLvEmbed =
            item.linkedVideoType === "youtube" ||
            item.linkedVideoType === "vimeo";
        itemWrap.classList.add("wcpg-stacked-lv-active");

        // Stop zoom immediately for all video types — the lens and cursor
        // overlay must not float over active video content.
        if (this.mainWrap === itemWrap) {
            this._zoomLeave();
            this.mainImg = null;
            this.mainWrap = null;
            this.mainDisplay = null;
        }

        if (isLvEmbed) {
            const iframe = itemWrap.querySelector(".wcpg-stacked-lv-iframe");
            if (iframe) {
                let src =
                    iframe.getAttribute("data-wcpg-lv-src") ||
                    item.linkedVideoEmbed ||
                    "";
                // User clicked play — always autoplay with mute (browser requirement)
                if (src && src.indexOf("autoplay=") === -1) {
                    src += (src.indexOf("?") === -1 ? "?" : "&") + "autoplay=1";
                    if (
                        src.indexOf("mute=") === -1 &&
                        src.indexOf("muted=") === -1
                    ) {
                        src += "&mute=1";
                    }
                }
                if (
                    item.linkedVideoType === "youtube" &&
                    this.opts.videoHideRelated &&
                    src.indexOf("rel=") === -1
                ) {
                    src += "&rel=0";
                }
                if (
                    item.linkedVideoType === "youtube" &&
                    !this.opts.videoEmbedControls &&
                    src.indexOf("controls=") === -1
                ) {
                    src += "&controls=0";
                }
                iframe.src = src;
            }
        } else {
            const video = itemWrap.querySelector(".wcpg-stacked-lv-video");
            if (video) {
                video.src = item.linkedVideo;
                video.muted = this.opts.videoHostedMuted;
                video.load();
                video.play().catch(() => {});
            }
        }
    },

    _stopStackedLinkedVideo(itemWrap, item) {
        if (!itemWrap || !itemWrap.classList.contains("wcpg-stacked-lv-active"))
            return;
        itemWrap.classList.remove("wcpg-stacked-lv-active");
        const isLvEmbed =
            item &&
            (item.linkedVideoType === "youtube" ||
                item.linkedVideoType === "vimeo");
        if (isLvEmbed) {
            const iframe = itemWrap.querySelector(".wcpg-stacked-lv-iframe");
            if (iframe) iframe.src = "";
        } else {
            const video = itemWrap.querySelector(".wcpg-stacked-lv-video");
            if (video) {
                video.pause();
                video.src = "";
            }
        }

        // Re-enter zoom immediately if the cursor is still over this item —
        // mouseenter won't re-fire because the pointer never left the element.
        if (
            this.opts.zoomOnHover &&
            !this._isMobile &&
            this._lastMouseEvent &&
            itemWrap.matches(":hover")
        ) {
            const img = itemWrap.querySelector(".wcpg-stacked-img");
            const i = this.stackedItems
                ? this.stackedItems.indexOf(itemWrap)
                : -1;
            if (img && i >= 0) {
                this.mainImg = img;
                this.mainWrap = itemWrap;
                this.mainDisplay = itemWrap;
                this.index = i;
                if (this.lens) itemWrap.appendChild(this.lens);
                if (this.winCursor) itemWrap.appendChild(this.winCursor);
                this._zoomEnter(this._lastMouseEvent);
            }
        }
    },

    _syncStackedVideo(prevIdx, newIdx) {
        // Pause/stop the previously active media
        if (prevIdx >= 0 && prevIdx !== newIdx) {
            const prevWrap = this.stackedItems && this.stackedItems[prevIdx];
            const prevItem = this.items[prevIdx];
            if (prevWrap && prevItem) {
                // Stop linked video if it was playing in the outgoing item
                this._stopStackedLinkedVideo(prevWrap, prevItem);
                if (prevItem.type === "video") {
                    const vid = prevWrap.querySelector(".wcpg-stacked-video");
                    if (vid) vid.pause();
                } else if (
                    prevItem.type === "youtube" ||
                    prevItem.type === "vimeo"
                ) {
                    // Use postMessage to pause in-place so the playback position
                    // is preserved — the iframe stays loaded, src is not cleared.
                    const ifr = prevWrap.querySelector(".wcpg-stacked-iframe");
                    if (ifr && ifr.contentWindow) {
                        try {
                            ifr.contentWindow.postMessage(
                                JSON.stringify(
                                    prevItem.type === "youtube"
                                        ? {
                                              event: "command",
                                              func: "pauseVideo",
                                              args: "",
                                          }
                                        : { method: "pause" },
                                ),
                                "*",
                            );
                        } catch (e) {}
                        ifr.setAttribute("data-wcpg-paused", "1");
                    }
                }
            }
        }
        // Activate the newly active item
        if (newIdx >= 0) {
            const newWrap = this.stackedItems && this.stackedItems[newIdx];
            const newItem = this.items[newIdx];
            if (newWrap && newItem) {
                if (newItem.type === "video" && this.opts.videoHostedAutoplay) {
                    const vid = newWrap.querySelector(".wcpg-stacked-video");
                    if (vid) vid.play().catch(() => {});
                } else if (
                    newItem.type === "youtube" ||
                    newItem.type === "vimeo"
                ) {
                    const ifr = newWrap.querySelector(".wcpg-stacked-iframe");
                    if (ifr) {
                        if (ifr.getAttribute("data-wcpg-paused") === "1") {
                            // Resume from the paused position via postMessage
                            ifr.removeAttribute("data-wcpg-paused");
                            if (ifr.contentWindow) {
                                try {
                                    ifr.contentWindow.postMessage(
                                        JSON.stringify(
                                            newItem.type === "youtube"
                                                ? {
                                                      event: "command",
                                                      func: "playVideo",
                                                      args: "",
                                                  }
                                                : { method: "play" },
                                        ),
                                        "*",
                                    );
                                } catch (e) {}
                            }
                        } else if (this.opts.videoAutoplay) {
                            // First activation with autoplay on — reload src with autoplay params
                            let src = newItem.embed || newItem.src;
                            if (src.indexOf("autoplay=") === -1) {
                                src +=
                                    (src.indexOf("?") === -1 ? "?" : "&") +
                                    "autoplay=1";
                                if (
                                    src.indexOf("mute=") === -1 &&
                                    src.indexOf("muted=") === -1
                                ) {
                                    src += "&mute=1";
                                }
                            }
                            if (
                                newItem.type === "youtube" &&
                                src.indexOf("enablejsapi=") === -1
                            ) {
                                src += "&enablejsapi=1";
                            }
                            ifr.src = src;
                        }
                    }
                }
            }
        }
    },

    _scrollToStackedItem(index) {
        const el = this.stackedItems && this.stackedItems[index];
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    _destroy(soft) {
        if (this._evZE) utils.off(this.mainWrap, "mouseenter", this._evZE);
        if (this._evZM) utils.off(this.mainWrap, "mousemove", this._evZM);
        if (this._evZL) utils.off(this.mainWrap, "mouseleave", this._evZL);
        if (this._evTS) utils.off(this.mainWrap, "touchstart", this._evTS);
        if (this._evTM) utils.off(this.mainWrap, "touchmove", this._evTM);
        if (this._evTE) utils.off(this.mainWrap, "touchend", this._evTE);
        this._destroyMainSwipe();
        if (this._evMainScrollNav) {
            if (this.mainWrap) this.mainWrap.removeEventListener("wheel", this._evMainScrollNav);
            this._evMainScrollNav = null;
        }
        if (this._scrollNavTimer) {
            clearTimeout(this._scrollNavTimer);
            this._scrollNavTimer = null;
        }
        this._scrollNavAccum = 0;
        if (this._evVidClick && this.mainVideo) {
            utils.off(this.mainVideo, "click", this._evVidClick);
            this._evVidClick = null;
        }
        if (this._touchActiveTimer) {
            clearTimeout(this._touchActiveTimer);
            this._touchActiveTimer = null;
        }
        if (this._zs?.raf) cancelAnimationFrame(this._zs.raf);
        if (this._zs?.momentumRaf) cancelAnimationFrame(this._zs.momentumRaf);
        if (this._mobileMq && this._mobileMqFn) {
            this._mobileMq.removeEventListener("change", this._mobileMqFn);
            this._mobileMq = null;
            this._mobileMqFn = null;
        }
        if (this._fitObserver) {
            this._fitObserver.disconnect();
            this._fitObserver = null;
        }
        if (this._stackMq && this._stackMqFn) {
            this._stackMq.removeEventListener("change", this._stackMqFn);
        }
        this._stackMq = null;
        this._stackMqFn = null;
        this.wrapper.classList.remove("wcpg-stacked");
        // ── Sticky Split cleanup ──
        this._destroySSEvents();
        this.ssStickyCol = null;
        this.wrapper.classList.remove("wcpg-layout-sticky-split");
        // ── Stacked layout cleanup ──
        this._destroyStackedSticky();
        this._destroyAnchorDotStickyFallback();
        this._destroyStackedThumbStickyFallback();
        if (this._stackedScrollFn) {
            window.removeEventListener("scroll", this._stackedScrollFn);
            window.removeEventListener("resize", this._stackedScrollFn);
            this._stackedScrollFn = null;
        }
        if (this._evStackedPG && this.pagination) {
            utils.off(this.pagination, "click", this._evStackedPG);
            this._evStackedPG = null;
        }
        if (this._evStackedClicks) {
            this._evStackedClicks.forEach(({ el, fn }) =>
                utils.off(el, "click", fn),
            );
            this._evStackedClicks = null;
        }
        if (this._stackedLvEvents) {
            this._stackedLvEvents.forEach(({ el, fn }) =>
                utils.off(el, "click", fn),
            );
            this._stackedLvEvents = null;
        }
        if (this._stackedZoomEvents) {
            this._stackedZoomEvents.forEach(({ el, event, fn }) =>
                utils.off(el, event, fn),
            );
            this._stackedZoomEvents = null;
        }
        this.wrapper.classList.remove("wcpg-layout-stacked");
        // ── Hierarchy Grid cleanup ──
        if (this._evHGClicks) {
            this._evHGClicks.forEach(({ el, fn }) => utils.off(el, "click", fn));
            this._evHGClicks = null;
        }
        if (this._hgLvEvents) {
            this._hgLvEvents.forEach(({ el, fn }) => utils.off(el, "click", fn));
            this._hgLvEvents = null;
        }
        if (this._hgZoomEvents) {
            this._hgZoomEvents.forEach(({ el, event, fn }) =>
                utils.off(el, event, fn),
            );
            this._hgZoomEvents = null;
        }
        this.wrapper.classList.remove("wcpg-layout-hierarchy-grid");
        // ── Tiles layout cleanup ──
        if (this._evTilesClicks) {
            this._evTilesClicks.forEach(({ el, fn }) => utils.off(el, "click", fn));
            this._evTilesClicks = null;
        }
        if (this._tilesZoomEvents) {
            this._tilesZoomEvents.forEach(({ el, event, fn }) => utils.off(el, event, fn));
            this._tilesZoomEvents = null;
        }
        if (this._evTilesShowMore) {
            if (this._tilesShowMoreBtn) utils.off(this._tilesShowMoreBtn, "click", this._evTilesShowMore);
            this._evTilesShowMore = null;
        }
        this.wrapper.classList.remove("wcpg-layout-tiles");
        this._thumbDragBound = false;
        window.removeEventListener("resize", this._resizeHandler);
        window.removeEventListener("scroll", this._scrollHandler);
        // Remove the zoom window from document.body (where it was appended
        // in _buildDOM to escape any parent overflow:hidden constraints).
        if (this.zoomWin && this.zoomWin.parentNode === document.body) {
            this.zoomWin.remove();
        }
        if (!soft) {
            [
                "wcpg-wrapper",
                "wcpg-thumb-bottom",
                "wcpg-thumb-top",
                "wcpg-thumb-left",
                "wcpg-thumb-right",
                "wcpg-layout-stacked",
                "wcpg-layout-sticky-split",
                "wcpg-layout-hierarchy-grid",
                "wcpg-layout-tiles",
                "wcpg-mobile-layout",
            ].forEach((c) => this.wrapper.classList.remove(c));
            [".wcpg-stage", ".wcpg-thumbnails-wrap", ".wcpg-row", ".wcpg-ss-row", ".wcpg-hg-wrap", ".wcpg-tiles-wrap"].forEach(
                (sel) =>
                    this.wrapper
                        .querySelectorAll(sel)
                        .forEach((n) => n.remove()),
            );
            // Clean up mobile layout observer
            if (this._mlMq && this._mlMqFn) {
                this._mlMq.removeEventListener("change", this._mlMqFn);
                this._mlMq = null;
                this._mlMqFn = null;
            }
            this._mobileLayoutActive = false;
        }
    },

    // ── Public instance API ───────────────────────────────────────────────────
    goTo(i) {
        this._goTo(i);
    },
    next() {
        this._goTo(this.index + 1);
    },
    prev() {
        this._goTo(this.index - 1);
    },
    openLightbox(i) {
        this._lbOpen(i != null ? i : this.index);
    },
    closeLightbox() {
        this._lbClose();
    },
    setOptions(o) {
        utils.extend(this.opts, o);
    },

    reinit(newOpts) {
        if (newOpts) utils.extend(this.opts, newOpts);
        this._destroy(true);
        this.items = [];
        this.index = 0;
        this._adaptiveMaxH = null;
        this._ts = {
            offset: 0,
            dragging: false,
            startPos: 0,
            startOffset: 0,
        };
        this._zs = {
            lensX: 0,
            lensY: 0,
            targetX: 0,
            targetY: 0,
            pctX: 0,
            pctY: 0,
            rect: null,
            raf: null,
            scale: 1,
            dist0: 0,
            scale0: 1,
            panX: 0,
            panY: 0,
            panX0: 0,
            panY0: 0,
            pinching: false,
        };
        this._collectItems();
        this._initMobileLayoutObserver();
        this._buildDOM();
        if (this.opts.galleryType === "anchor" && !this._mobileLayoutActive) {
            this._bindStackedEvents();
        } else if (this.opts.galleryType === "sticky-split" && !this._mobileLayoutActive) {
            this._bindStickySplitEvents();
            if (this.opts.preloadImages) this._preloadAll();
        } else if (this.opts.galleryType === "hierarchy-grid" && !this._mobileLayoutActive) {
            this._bindHierarchyGridEvents();
            if (this.opts.preloadImages) this._preloadAll();
        } else if (this.opts.galleryType === "tiles" && !this._mobileLayoutActive) {
            this._bindTilesEvents();
            if (this.opts.preloadImages) this._preloadAll();
        } else {
            this._bindEvents();
            if (this.opts.preloadImages) this._preloadAll();
        }
    },

    destroy() {
        this._destroy(false);
    },

    // ── Variation image support ───────────────────────────────────────────────

    /**
     * Swap in a variation image at position 0 of the gallery.
     * If a variation item is already active it is updated in place;
     * otherwise a new item is prepended so the original gallery is preserved.
     * Call resetVariationImage() to restore the original state.
     *
     * @param {string} src      - Display/thumbnail URL of the variation image
     * @param {string} zoomSrc  - Full-size URL used for zoom (falls back to src)
     */
    // ── Stacked layout: rebuild items + re-bind all stacked events ───────────
    _refreshStackedView() {
        // Remove per-item and global stacked listeners before rebuilding
        this._destroyStackedSticky();
        this._destroyAnchorDotStickyFallback();
        this._destroyStackedThumbStickyFallback();
        if (this._stackedScrollFn) {
            window.removeEventListener("scroll", this._stackedScrollFn);
            window.removeEventListener("resize", this._stackedScrollFn);
            this._stackedScrollFn = null;
        }
        if (this._evStackedPG && this.pagination) {
            utils.off(this.pagination, "click", this._evStackedPG);
            this._evStackedPG = null;
        }
        if (this._evStackedClicks) {
            this._evStackedClicks.forEach(({ el, fn }) =>
                utils.off(el, "click", fn),
            );
            this._evStackedClicks = null;
        }
        if (this._stackedLvEvents) {
            this._stackedLvEvents.forEach(({ el, fn }) =>
                utils.off(el, "click", fn),
            );
            this._stackedLvEvents = null;
        }
        if (this._stackedZoomEvents) {
            this._stackedZoomEvents.forEach(({ el, event, fn }) =>
                utils.off(el, event, fn),
            );
            this._stackedZoomEvents = null;
        }
        // Remove thumb navigation events so _bindStackedEvents() can re-add them
        if (this._evTC && this.thumbsList) {
            utils.off(this.thumbsList, "click", this._evTC);
            this._evTC = null;
        }
        if (this._evTH && this.thumbsList) {
            utils.off(this.thumbsList, "mouseover", this._evTH);
            this._evTH = null;
        }
        if (this._evTK && this.thumbsList) {
            utils.off(this.thumbsList, "keydown", this._evTK);
            this._evTK = null;
        }
        if (this._evTP && this.thumbPrev) {
            utils.off(this.thumbPrev, "click", this._evTP);
            this._evTP = null;
        }
        if (this._evTN && this.thumbNext) {
            utils.off(this.thumbNext, "click", this._evTN);
            this._evTN = null;
        }
        if (this._evThumbScroll && this.thumbsTrack) {
            this.thumbsTrack.removeEventListener("wheel", this._evThumbScroll);
            this._evThumbScroll = null;
        }

        // Reset ephemeral state
        this.index = 0;
        this.stackedItems = [];
        this._stackedLbTriggers = [];
        this._zoomLeave();
        this.mainImg = null;
        this.mainWrap = null;
        this.mainDisplay = null;

        // Rebuild all stacked item elements
        if (this.stackedWrap) {
            this.stackedWrap.innerHTML = "";
            this.items.forEach((item, i) => {
                const itemWrap = this._buildSingleStackedItem(item, i);
                this.stackedWrap.appendChild(itemWrap);
                this.stackedItems.push(itemWrap);
            });
        }

        // Rebuild pagination dots inside the existing container (dots mode)
        if (this.pagination && this.opts.stackedNavigationType !== "thumbs") {
            this.pagination.innerHTML = "";
            this.paginationDots = [];
            this.items.forEach((_, i) => {
                const dot = utils.el(
                    "button",
                    "wcpg-dot" + (i === 0 ? " wcpg-dot-active" : ""),
                );
                dot.setAttribute("aria-label", `Go to image ${i + 1}`);
                dot.setAttribute("data-index", i);
                this.pagination.appendChild(dot);
                this.paginationDots.push(dot);
            });
        }

        // Rebuild thumbnail items in-place (thumbs mode)
        if (this.opts.stackedNavigationType === "thumbs" && this.thumbsList) {
            this.thumbsList.innerHTML = "";
            this.thumbEls = [];
            if (this._ts) {
                this._ts.offset = 0;
                this._ts.loopAnimating = false;
                this._ts.scrollWrapAnimating = false;
            }
            this._applyThumbOffset(false);
            if (this.items.length > 1) {
                this._buildThumbItems();
            }
            this._updateThumbActive(0);
            requestAnimationFrame(() => {
                this._updateThumbButtons();
                this._updateFadeMask();
            });
        }

        // Re-bind events (also re-registers scroll/resize + initial video sync)
        this._bindStackedEvents();
    },

    // ── Stacked layout: sticky product summary ────────────────────────────────

    _initStackedSticky() {
        const bp = this.opts.stackedStickyBreakpoint || 0;

        if (bp > 0) {
            // Watch for viewport width changes and toggle sticky accordingly
            this._stickyBpMq = window.matchMedia(`(max-width: ${bp - 1}px)`);
            this._stickyBpMqFn = (e) => {
                if (e.matches) {
                    this._disableStackedSticky();
                } else {
                    this._enableStackedSticky();
                }
            };
            this._stickyBpMq.addEventListener("change", this._stickyBpMqFn);
            // Apply immediately based on current viewport
            if (!this._stickyBpMq.matches) {
                this._enableStackedSticky();
            }
        } else {
            // No breakpoint restriction — always active
            this._enableStackedSticky();
        }
    },

    _enableStackedSticky() {
        if (this._stickyActive) return;

        const selector = (
            this.opts.stackedStickySelector || ".summary.entry-summary"
        ).trim();
        const summaryEl = document.querySelector(selector);
        if (!summaryEl) return;

        this._stickyActive = true;
        this._stickySummaryEl = summaryEl;

        // Fix any ancestors that would block position:sticky, then let CSS handle it.
        if (this.opts.stackedStickyFixOverflow !== false) {
            this._stickyOverflowPatches = this._patchAncestorOverflow(summaryEl);
        }

        const stickyTop = this.opts.stackedStickyTop ?? 100;
        summaryEl.style.setProperty("--wcpg-summary-sticky-top", stickyTop + "px");
        summaryEl.classList.add("wcpg-summary-sticky");

        const _galleryRef = this.stackedWrap || this.ssScrollWrap || this.wrapper;
        if (_galleryRef) {
            let summaryH = summaryEl.offsetHeight;
            let lastTop = stickyTop;

            // Drive --wcpg-summary-sticky-top so the element tracks the gallery
            // end naturally: while the gallery is in view the value stays at
            // stickyTop; once the bottom approaches the value decreases (goes
            // negative if needed) and the sticky element glides upward with the
            // gallery instead of jumping.  Only write the CSS variable when the
            // value actually changes to avoid unnecessary style invalidations.
            this._stickyScrollFn = () => {
                const _ref = this.stackedWrap || this.ssScrollWrap || this.wrapper;
                if (!this._stickySummaryEl || !_ref) return;
                const galleryBottom =
                    _ref.getBoundingClientRect().bottom;
                const newTop = Math.min(stickyTop, galleryBottom - summaryH);
                if (newTop !== lastTop) {
                    lastTop = newTop;
                    this._stickySummaryEl.style.setProperty(
                        "--wcpg-summary-sticky-top",
                        newTop + "px",
                    );
                }
            };

            this._stickyResizeFn = () => {
                summaryH = summaryEl.offsetHeight;
                this._stickyScrollFn();
            };

            window.addEventListener("scroll", this._stickyScrollFn, {
                passive: true,
            });
            window.addEventListener("resize", this._stickyResizeFn, {
                passive: true,
            });
            this._stickyScrollFn(); // apply immediately for current scroll position
        }
    },

    _disableStackedSticky() {
        if (!this._stickyActive) return;
        this._stickyActive = false;

        if (this._stickyScrollFn) {
            window.removeEventListener("scroll", this._stickyScrollFn);
            this._stickyScrollFn = null;
        }
        if (this._stickyResizeFn) {
            window.removeEventListener("resize", this._stickyResizeFn);
            this._stickyResizeFn = null;
        }

        this._restoreAncestorOverflow(this._stickyOverflowPatches);
        this._stickyOverflowPatches = null;

        if (this._stickySummaryEl) {
            this._stickySummaryEl.classList.remove("wcpg-summary-sticky");
            this._stickySummaryEl.style.removeProperty("--wcpg-summary-sticky-top");
            this._stickySummaryEl = null;
        }
    },

    _destroyStackedSticky() {
        this._disableStackedSticky();
        if (this._stickyBpMq && this._stickyBpMqFn) {
            this._stickyBpMq.removeEventListener("change", this._stickyBpMqFn);
            this._stickyBpMq = null;
            this._stickyBpMqFn = null;
        }
    },

    // ── Overflow ancestor utilities ───────────────────────────────────────────

    _hasOverflowAncestor(el) {
        let p = el.parentElement;
        while (p && p !== document.documentElement) {
            const s = window.getComputedStyle(p);
            if (
                s.overflowX === "hidden" ||
                s.overflowX === "clip" ||
                s.overflowY === "hidden" ||
                s.overflowY === "clip"
            )
                return true;
            p = p.parentElement;
        }
        return false;
    },

    // Walk ancestors from el up to <html>. For any element whose computed
    // overflow-x or overflow-y is hidden/clip (which breaks position:sticky),
    // override it to visible via an inline style. Returns an array of patch
    // records so the change can be fully undone by _restoreAncestorOverflow.
    _patchAncestorOverflow(el) {
        const patches = [];
        let node = el.parentElement;
        while (node && node !== document.documentElement) {
            const s = window.getComputedStyle(node);
            const ox = s.overflowX;
            const oy = s.overflowY;
            const needsX = ox === "hidden" || ox === "clip";
            const needsY = oy === "hidden" || oy === "clip";
            if (needsX || needsY) {
                patches.push({
                    node,
                    prevX: node.style.overflowX,
                    prevY: node.style.overflowY,
                    changedX: needsX,
                    changedY: needsY,
                });
                if (needsX) node.style.overflowX = "visible";
                if (needsY) node.style.overflowY = "visible";
            }
            node = node.parentElement;
        }
        return patches;
    },

    // Undo all inline-style overrides applied by _patchAncestorOverflow.
    _restoreAncestorOverflow(patches) {
        if (!patches) return;
        patches.forEach(({ node, prevX, prevY, changedX, changedY }) => {
            if (changedX) {
                if (prevX) node.style.overflowX = prevX;
                else node.style.removeProperty("overflow-x");
            }
            if (changedY) {
                if (prevY) node.style.overflowY = prevY;
                else node.style.removeProperty("overflow-y");
            }
        });
    },

    // ── Anchor dot sticky ─────────────────────────────────────────────────────
    // If any ancestor clips overflow, patch it so position:sticky can reach
    // the viewport. CSS handles all positioning — no scroll listener needed.
    _initAnchorDotStickyFallback() {
        if (this.opts.stackedStickyFixOverflow === false) return;
        if (!this.pagination || !this._anchorRail) return;
        if (!this._hasOverflowAncestor(this.pagination)) return;
        this._dotOverflowPatches = this._patchAncestorOverflow(this.pagination);
    },

    _destroyAnchorDotStickyFallback() {
        this._restoreAncestorOverflow(this._dotOverflowPatches);
        this._dotOverflowPatches = null;
    },

    // ── Stacked thumbnail sidebar sticky ─────────────────────────────────────
    // If any ancestor clips overflow, patch it so position:sticky can reach
    // the viewport. CSS handles all positioning — no scroll listener needed.
    _initStackedThumbStickyFallback() {
        if (this.opts.stackedStickyFixOverflow === false) return;
        if (!this.thumbsWrap) return;
        const pos = this.opts.thumbnailPosition;
        if (pos !== "left" && pos !== "right") return;
        if (!this._hasOverflowAncestor(this.thumbsWrap)) return;
        this._thumbOverflowPatches = this._patchAncestorOverflow(this.thumbsWrap);
    },

    _destroyStackedThumbStickyFallback() {
        this._restoreAncestorOverflow(this._thumbOverflowPatches);
        this._thumbOverflowPatches = null;
    },

    swapVariationImage(src, zoomSrc, thumbSrc) {
        if (!src) return;

        // ── Restore from an active gallery swap ───────────────────────────
        // Any previously active full gallery swap must be torn down before we
        // can search for an existing item or overwrite items[0].
        if (this._originalItems) {
            this.items = [...this._originalItems];
            this._originalItems = null;
            this._hasVariationItem = false;
            // Rebuild DOM so it reflects the restored items.
            if (this.opts.galleryType === "anchor" && !this._mobileLayoutActive) {
                this._refreshStackedView();
            } else if (
                this.opts.galleryType === "sticky-split" &&
                !this._mobileLayoutActive
            ) {
                this._refreshSSView();
            } else if (
                this.opts.galleryType === "hierarchy-grid" &&
                !this._mobileLayoutActive
            ) {
                this._refreshHierarchyGridView();
            } else if (
                this.opts.galleryType === "tiles" &&
                !this._mobileLayoutActive
            ) {
                this._refreshTilesView();
            } else {
                this._refreshGalleryView();
            }
        }

        // ── Restore from an active single-image swap ──────────────────────
        // Switching directly between two single-image variations: undo the
        // previous temporary overwrite so items[0] is the original product item.
        if (this._varMode === "swapped" && this._varSwapOrigItem) {
            this.items[0] = this._varSwapOrigItem;
            this._varSwapOrigItem = null;
            this._varMode = null;
        }

        // Normalise URLs for comparison: strip query strings and WP size suffixes.
        const normUrl = (u) =>
            u.split("?")[0].replace(/-\d+x\d+(\.[^.]+)$/, "$1");
        const normSrc = normUrl(src);

        // ── Case A: variation image already exists in the gallery ─────────
        // Just navigate to that item — no items/DOM changes needed.
        const existingIdx = this.items.findIndex(
            (it) => it.type === "image" && normUrl(it.src) === normSrc,
        );
        if (existingIdx !== -1) {
            this._varMode = "found";
            this._goTo(existingIdx);
            return;
        }

        // ── Case B: variation image is not in the gallery ─────────────────
        // Overwrite items[0] temporarily so the main viewer and zoom both
        // use the variation image. Thumbnails are deselected (not modified).
        const varItem = {
            src,
            zoomSrc: zoomSrc || src,
            thumb: thumbSrc || src,
            caption: "",
            type: "image",
            poster: "",
            placeholder: "",
            w: 0,
            h: 0,
            linkedVideoType: "",
            linkedVideoEmbed: "",
            linkedVideo: "",
        };

        this._varMode = "swapped";
        this._varSwapOrigItem = { ...this.items[0] };
        this.items[0] = varItem;

        // ── Anchor ────────────────────────────────────────────────────────
        if (this.opts.galleryType === "anchor") {
            // Stacked layout shows all items inline — refresh so item 0
            // in the stack displays the variation image.
            this._refreshStackedView();
            return;
        }

        // ── Sticky Split ──────────────────────────────────────────────────
        if (this.opts.galleryType === "sticky-split" && !this._mobileLayoutActive) {
            // Rebuild scroll column (item 0 shows variation image) and render
            // the variation in the sticky viewer.
            this._refreshSSView();
            this._scrollSSItemToView(0);
            return;
        }

        // ── Hierarchy Grid ────────────────────────────────────────────────
        if (this.opts.galleryType === "hierarchy-grid" && !this._mobileLayoutActive) {
            this._refreshHierarchyGridView();
            return;
        }

        // ── Tiles ─────────────────────────────────────────────────────────
        if (this.opts.galleryType === "tiles" && !this._mobileLayoutActive) {
            this._refreshTilesView();
            return;
        }

        // ── Carousel ──────────────────────────────────────────────────────
        // Render variation image in the main viewer. Deselect all thumbnails
        // (the thumb at index 0 still shows the original product image in the
        // DOM, which is correct — no thumb corresponds to this variation).
        if (this.index === 0) {
            this._renderItem(0, false);
        } else {
            this._goTo(0);
        }
        this._updateThumbActive(-1);
    },

    /**
     * Restore the gallery to its state before a variation was selected.
     */
    resetVariationImage() {
        // ── "found" mode: we just navigated to an existing gallery item ───
        // Nothing was modified — navigate back to item 0 and clear the mode.
        if (this._varMode === "found") {
            this._varMode = null;
            this._goTo(0);
            return;
        }

        // ── "swapped" mode: items[0] was temporarily overwritten ──────────
        if (this._varMode === "swapped") {
            if (this._varSwapOrigItem) {
                this.items[0] = this._varSwapOrigItem;
                this._varSwapOrigItem = null;
            }
            this._varMode = null;

            if (this.opts.galleryType === "anchor" && !this._mobileLayoutActive) {
                this._refreshStackedView();
                return;
            }
            if (
                this.opts.galleryType === "sticky-split" &&
                !this._mobileLayoutActive
            ) {
                this._refreshSSView();
                this._scrollSSItemToView(0);
                return;
            }
            if (
                this.opts.galleryType === "hierarchy-grid" &&
                !this._mobileLayoutActive
            ) {
                this._refreshHierarchyGridView();
                return;
            }
            if (
                this.opts.galleryType === "tiles" &&
                !this._mobileLayoutActive
            ) {
                this._refreshTilesView();
                return;
            }
            // Carousel: re-render item 0 and restore its thumbnail as active.
            if (this.index === 0) {
                this._renderItem(0, false);
                this._updateThumbActive(0);
            } else {
                this._goTo(0);
            }
            return;
        }
    },

    /**
     * Replace the entire gallery with a variation-specific set of images.
     *
     * The original gallery items are preserved and will be restored when
     * resetVariationGallery() is called. Calling this multiple times (e.g.
     * switching between variations) updates the gallery in-place without
     * accumulating restore snapshots.
     *
     * @param {Array} galleryItems  Array of objects from PHP:
     *   { main_src, thumb_src, full_src, caption, width, height }
     */
    swapVariationGallery(galleryItems) {
        if (!galleryItems || !galleryItems.length) return;

        // If a single-image variation is currently prepended, remove it before
        // snapshotting so _originalItems always captures the clean product gallery.
        if (this._hasVariationItem) {
            this.items.shift();
            this._hasVariationItem = false;
        }

        // If a single-image swap is active (items[0] temporarily overwritten),
        // restore items[0] from backup before snapshotting so the snapshot
        // captures the original product item, not the variation image.
        if (this._varMode === "swapped" && this._varSwapOrigItem) {
            this.items[0] = this._varSwapOrigItem;
            this._varSwapOrigItem = null;
        }
        this._varMode = null;

        // Snapshot the original items only on the first call so that
        // switching between two variations with galleries still restores
        // the original product gallery correctly.
        if (!this._originalItems) {
            this._originalItems = [...this.items];
        }

        this.items = galleryItems.map((g) => ({
            src: g.main_src,
            zoomSrc: g.full_src || g.main_src,
            thumb: g.thumb_src || g.main_src,
            caption: g.caption || "",
            type: "image",
            poster: "",
            w: g.width || 0,
            h: g.height || 0,
            linkedVideoType: g.video_type || "",
            linkedVideoEmbed: g.video_embed || "",
            linkedVideo: g.video_src || "",
        }));

        // Clear the single-image variation flag — the gallery is now
        // a full replacement, not a prepended item.
        this._hasVariationItem = false;

        // ── Stacked layout path ──────────────────────────────────────────
        if (this.opts.galleryType === "anchor" && !this._mobileLayoutActive) {
            this._refreshStackedView();
            return;
        }

        // ── Sticky Split path ─────────────────────────────────────────────
        if (this.opts.galleryType === "sticky-split" && !this._mobileLayoutActive) {
            this._refreshSSView();
            this._scrollSSItemToView(0);
            return;
        }

        // ── Hierarchy Grid path ───────────────────────────────────────────
        if (this.opts.galleryType === "hierarchy-grid" && !this._mobileLayoutActive) {
            this._refreshHierarchyGridView();
            return;
        }

        // ── Tiles path ────────────────────────────────────────────────────
        if (this.opts.galleryType === "tiles" && !this._mobileLayoutActive) {
            this._refreshTilesView();
            return;
        }

        this._refreshGalleryView();
    },

    /**
     * Restore the original gallery after a swapVariationGallery() call.
     * No-op if no gallery swap is currently active.
     */
    resetVariationGallery() {
        if (!this._originalItems) return;

        this.items = this._originalItems;
        this._originalItems = null;
        this._hasVariationItem = false;

        // ── Stacked layout path ──────────────────────────────────────────
        if (this.opts.galleryType === "anchor" && !this._mobileLayoutActive) {
            this._refreshStackedView();
            return;
        }

        // ── Sticky Split path ─────────────────────────────────────────────
        if (this.opts.galleryType === "sticky-split" && !this._mobileLayoutActive) {
            this._refreshSSView();
            this._scrollSSItemToView(0);
            return;
        }

        // ── Hierarchy Grid path ───────────────────────────────────────────
        if (this.opts.galleryType === "hierarchy-grid" && !this._mobileLayoutActive) {
            this._refreshHierarchyGridView();
            return;
        }

        // ── Tiles path ────────────────────────────────────────────────────
        if (this.opts.galleryType === "tiles" && !this._mobileLayoutActive) {
            this._refreshTilesView();
            return;
        }

        this._refreshGalleryView();
    },

    // ── Sticky Split layout methods ───────────────────────────────────────────

    _initMobileLayoutObserver() {
        // Collapse anchor/sticky-split to a standard carousel below mobileBreakpoint.
        // Remove any previous listener first (idempotent across reinit calls).
        if (this._mlMq && this._mlMqFn) {
            this._mlMq.removeEventListener("change", this._mlMqFn);
        }
        this._mlMq = null;
        this._mlMqFn = null;

        const bp = this.opts.mobileBreakpoint;
        if (!bp || bp <= 0) {
            this._mobileLayoutActive = false;
            return;
        }

        this._mlMq = window.matchMedia(`(max-width: ${bp - 1}px)`);
        // Set initial state synchronously before _buildDOM runs.
        this._mobileLayoutActive = this._mlMq.matches;

        this._mlMqFn = (e) => {
            const nowMobile = e.matches;
            if (nowMobile === this._mobileLayoutActive) return;
            this._mobileLayoutActive = nowMobile;
            this.reinit();
        };
        this._mlMq.addEventListener("change", this._mlMqFn);
    },

    _bindStickySplitEvents() {
        // Capture the sticky viewer refs before any per-item loops so the
        // scroll-item zoom closures can restore them on mouseleave.
        const _ssOrigMainWrap    = this.mainWrap;
        const _ssOrigMainImg     = this.mainImg;
        const _ssOrigMainDisplay = this.mainDisplay;

        // Zoom on sticky main viewer (mirrors _bindEvents)
        if (this.opts.zoomOnHover) {
            utils.on(this.mainWrap, "mouseenter", (this._evZE = (e) => this._zoomEnter(e)));
            utils.on(this.mainWrap, "mousemove",  (this._evZM = (e) => this._zoomMove(e)));
            utils.on(this.mainWrap, "mouseleave", (this._evZL = () => this._zoomLeave()));
        }
        // Touch on sticky main viewer
        if (this.opts.touchZoom) {
            utils.on(this.mainWrap, "touchstart", (this._evTS = (e) => this._touchStart(e)), { passive: false });
            utils.on(this.mainWrap, "touchmove",  (this._evTM = (e) => this._touchMove(e)), { passive: false });
            utils.on(this.mainWrap, "touchend",   (this._evTE = (e) => this._touchEnd(e)));
        }
        if (
            this.opts.mainImageSwipe &&
            !this.opts.zoomOnHover &&
            !this.opts.touchZoom &&
            this.items.length > 1
        ) {
            this._bindMainSwipe(this.mainWrap);
        }

        // Lightbox trigger on main viewer
        if (this.lbTrigger) {
            utils.on(this.lbTrigger, "click", (this._evLbTrigger = () => {
                this._lbOpen(this.index);
            }));
        }

        // Open lightbox on main image click (lightboxOpenOnImageClick)
        if (this.opts.lightbox && this.opts.lightboxOpenOnImageClick) {
            utils.on(
                this.mainWrap,
                "click",
                (this._evImgClick = (e) => {
                    if (e.target.closest(".wcpg-lb-trigger")) return;
                    if (e.target.closest(".wcpg-pagination")) return;
                    if (this._zs.pinching) return;
                    if (this._suppressClick) return;
                    this._lbOpen(this.index);
                }),
            );
            if (!this._isMobile) this.mainWrap.style.cursor = "zoom-in";
        }

        // Main image arrows
        if (this.mainPrev) {
            utils.on(this.mainPrev, "click", (this._evMainPrev = () => this._goTo(this.index - 1)));
        }
        if (this.mainNext) {
            utils.on(this.mainNext, "click", (this._evMainNext = () => this._goTo(this.index + 1)));
        }

        // Pagination dots / count nav
        if (this.pagination) {
            utils.on(this.pagination, "click", (this._evPgSS = (e) => {
                const dot = e.target.closest(".wcpg-dot");
                if (dot) { this._goTo(parseInt(dot.dataset.index)); return; }
                if (e.target.closest(".wcpg-count-prev")) { this._goTo(this.index - 1); return; }
                if (e.target.closest(".wcpg-count-next")) { this._goTo(this.index + 1); return; }
            }));
        }

        // Click handlers on scroll items (lightbox or navigation)
        this._ssClickFns = [];
        if (this._ssItemEls) {
            const clickable = this.opts.stickySplitClickAction === "lightbox" && this.opts.lightbox;
            this._ssItemEls.forEach((itemEl, i) => {
                const fn = () => {
                    // Optionally swap main viewer on click (independent of lightbox)
                    if (this.opts.stickySplitClickSync && i !== this.index) {
                        this.index = i;
                        this._renderItem(i, true);
                        this._updatePagination(i);
                    }
                    // Open lightbox if configured, zooming from the clicked scroll item.
                    // Use the img if present (image items and video items with a thumbnail);
                    // fall back to itemEl itself for video items that have no thumbnail img.
                    if (clickable) {
                        const itemImg = this._ssItemEls[i] && this._ssItemEls[i].querySelector("img");
                        this._lbSourceOverride = itemImg || itemEl;
                        this._lbOpen(i);
                    }
                };
                utils.on(itemEl, "click", fn);
                this._ssClickFns.push({ el: itemEl, fn });
            });
        }

        // Zoom on scroll column items (when stickySplitThumbZoom is enabled)
        if (this.opts.stickySplitThumbZoom && this.opts.zoomOnHover && !this._isMobile) {
            this._ssItemZoomEvents = [];
            this._ssItemEls && this._ssItemEls.forEach((itemEl, i) => {
                const item = this.items[i];
                if (!item || item.type !== "image") return;
                const img = itemEl.querySelector(".wcpg-ss-img");
                if (!img) return;

                const enterFn = (e) => {
                    // Save the sticky viewer's current index so we can restore it
                    // on leave unless click-sync changed it during the hover.
                    this._ssZoomSavedIndex = this.index;
                    this.index = i;
                    this.mainImg     = img;
                    this.mainWrap    = itemEl;
                    this.mainDisplay = itemEl;
                    if (this.lens)      itemEl.appendChild(this.lens);
                    if (this.winCursor) itemEl.appendChild(this.winCursor);
                    this._zoomEnter(e);
                };
                const moveFn  = (e) => this._zoomMove(e);
                const leaveFn = () => {
                    this._zoomLeave();
                    // Restore index only if click-sync did not change it mid-hover
                    if (this.index === i && this._ssZoomSavedIndex !== undefined) {
                        this.index = this._ssZoomSavedIndex;
                    }
                    this._ssZoomSavedIndex = undefined;
                    // Restore sticky viewer DOM refs
                    this.mainWrap    = _ssOrigMainWrap;
                    this.mainImg     = _ssOrigMainImg;
                    this.mainDisplay = _ssOrigMainDisplay;
                    if (this.lens      && _ssOrigMainWrap) _ssOrigMainWrap.appendChild(this.lens);
                    if (this.winCursor && _ssOrigMainWrap) _ssOrigMainWrap.appendChild(this.winCursor);
                };

                utils.on(itemEl, "mouseenter", enterFn);
                utils.on(itemEl, "mousemove",  moveFn);
                utils.on(itemEl, "mouseleave", leaveFn);
                this._ssItemZoomEvents.push(
                    { el: itemEl, event: "mouseenter", fn: enterFn },
                    { el: itemEl, event: "mousemove",  fn: moveFn  },
                    { el: itemEl, event: "mouseleave", fn: leaveFn },
                );
            });
        }

        // Main image load events — restore zoom after video → image transitions
        utils.on(this.mainVideo, "click", (this._evSSVidClick = (e) => {
            if (this.opts.videoControls) return;
            e.stopPropagation();
            if (this.mainVideo.paused) {
                this.mainVideo.play().catch(() => {});
            } else {
                this.mainVideo.pause();
            }
        }));
        utils.on(this.mainImg, "loadstart", (this._evSSImgLoadStart = () => this._showSpinner()));
        utils.on(this.mainImg, "load", (this._evSSImgLoad = () => {
            this._hideSpinner();
            this._hidePlaceholder();
            if (!this.mainWrap.classList.contains("wcpg-linked-video-active")) {
                this.mainWrap.classList.remove("wcpg-no-zoom");
                if (this.lens) this.lens.style.visibility = "";
            }
            const _hm = this.opts.mainImageHeightMode;
            if (_hm === "lock") this._lockMainHeight();
            else if (_hm === "adaptive") this._updateAdaptiveHeight();
        }));

        // Sync no-thumb items to a sibling image item's height
        const noThumbEls = this._ssItemEls
            ? this._ssItemEls.filter((el) => el.classList.contains("wcpg-ss-no-thumb"))
            : [];
        if (noThumbEls.length) {
            // Run once immediately (will hit 0 before images load, which is fine —
            // the ResizeObserver below fires again once the image has dimensions).
            this._syncSSNoThumbHeight();

            // Pick the nearest sibling that has a real image
            const refEl = this._ssItemEls.find(
                (el) => !el.classList.contains("wcpg-ss-no-thumb") && el.querySelector(".wcpg-ss-img"),
            );
            if (refEl) {
                this._ssNoThumbRO = new ResizeObserver(() => this._syncSSNoThumbHeight());
                this._ssNoThumbRO.observe(refEl);
            }
        }

        // Sticky product summary (reuses stacked sticky infrastructure)
        if (this.opts.stackedStickyEnabled) {
            this._initStackedSticky();
        }

        // Show/hide the wrapper mobile class
        this.wrapper.classList.toggle("wcpg-mobile-layout", !!this._mobileLayoutActive);
    },

    _bindMainSwipe(el) {
        let _startX = 0, _startY = 0, _dir = null, _dragging = false;
        this._swipeEl = el;

        // ── Touch ──────────────────────────────────────────────────────────────
        this._evSwipeStart = (e) => {
            const t = e.touches[0];
            _startX = t.clientX;
            _startY = t.clientY;
            _dir = null;
            if (this.mainWrap) this.mainWrap.classList.add("wcpg-touch-active");
            if (this.stage) this.stage.classList.add("wcpg-touch-active");
            if (this._touchActiveTimer) clearTimeout(this._touchActiveTimer);
            this._touchActiveTimer = setTimeout(() => {
                if (this.mainWrap) this.mainWrap.classList.remove("wcpg-touch-active");
                if (this.stage) this.stage.classList.remove("wcpg-touch-active");
            }, 3000);
        };

        // Non-passive: calling preventDefault() on horizontal gestures stops
        // the page from scrolling so touchend coordinates are reliable.
        this._evSwipeMove = (e) => {
            if (_dir) return;
            const t = e.touches[0];
            const dx = Math.abs(t.clientX - _startX);
            const dy = Math.abs(t.clientY - _startY);
            if (dx < 5 && dy < 5) return;
            _dir = dx >= dy ? "h" : "v";
            if (_dir === "h") e.preventDefault();
        };

        this._evSwipeEnd = (e) => {
            if (_dir !== "h") return;
            const t = e.changedTouches[0];
            if (!t) return;
            const dx = t.clientX - _startX;
            if (Math.abs(dx) < this.opts.swipeThreshold) return;
            this._goTo(dx < 0 ? this.index + 1 : this.index - 1);
        };

        this._evSwipeCancel = () => { _dir = null; };

        // ── Mouse drag (desktop) ───────────────────────────────────────────────
        this._evMouseDown = (e) => {
            if (e.button !== 0) return;
            if (e.target.closest(".wcpg-main-arrow,.wcpg-pagination,.wcpg-lb-trigger")) return;
            _dragging = true;
            _startX = e.clientX;
            _startY = e.clientY;
            _dir = null;
            el.style.cursor = "grabbing";
        };

        this._evMouseMove = (e) => {
            if (!_dragging || _dir) return;
            const dx = Math.abs(e.clientX - _startX);
            const dy = Math.abs(e.clientY - _startY);
            if (dx < 5 && dy < 5) return;
            _dir = dx >= dy ? "h" : "v";
        };

        this._evMouseUp = (e) => {
            if (!_dragging) return;
            _dragging = false;
            el.style.cursor = "grab";
            if (_dir !== "h") return;
            const dx = e.clientX - _startX;
            if (Math.abs(dx) < this.opts.swipeThreshold) return;
            // Suppress the click that fires after mouseup so lightbox doesn't open
            this._suppressClick = true;
            setTimeout(() => { this._suppressClick = false; }, 0);
            this._goTo(dx < 0 ? this.index + 1 : this.index - 1);
        };

        this._evMouseLeave = () => {
            if (_dragging) {
                _dragging = false;
                el.style.cursor = "grab";
            }
        };

        // Set grab cursor to signal draggability
        el.style.cursor = "grab";

        utils.on(el, "touchstart",  this._evSwipeStart,  { passive: true });
        utils.on(el, "touchmove",   this._evSwipeMove,   { passive: false });
        utils.on(el, "touchend",    this._evSwipeEnd,    { passive: true });
        utils.on(el, "touchcancel", this._evSwipeCancel, { passive: true });
        utils.on(el, "mousedown",   this._evMouseDown);
        utils.on(el, "mousemove",   this._evMouseMove);
        utils.on(el, "mouseup",     this._evMouseUp);
        utils.on(el, "mouseleave",  this._evMouseLeave);
    },

    _destroyMainSwipe() {
        if (this._swipeEl) {
            if (this._evSwipeStart)  utils.off(this._swipeEl, "touchstart",  this._evSwipeStart);
            if (this._evSwipeMove)   utils.off(this._swipeEl, "touchmove",   this._evSwipeMove);
            if (this._evSwipeEnd)    utils.off(this._swipeEl, "touchend",    this._evSwipeEnd);
            if (this._evSwipeCancel) utils.off(this._swipeEl, "touchcancel", this._evSwipeCancel);
            if (this._evMouseDown)   utils.off(this._swipeEl, "mousedown",   this._evMouseDown);
            if (this._evMouseMove)   utils.off(this._swipeEl, "mousemove",   this._evMouseMove);
            if (this._evMouseUp)     utils.off(this._swipeEl, "mouseup",     this._evMouseUp);
            if (this._evMouseLeave)  utils.off(this._swipeEl, "mouseleave",  this._evMouseLeave);
            this._swipeEl.style.cursor = "";
        }
        this._swipeEl = null;
        this._evSwipeStart = null;
        this._evSwipeMove = null;
        this._evSwipeEnd = null;
        this._evSwipeCancel = null;
        this._evMouseDown = null;
        this._evMouseMove = null;
        this._evMouseUp = null;
        this._evMouseLeave = null;
    },

    _destroySSEvents() {
        // Zoom/touch (mirrored from _destroy for when we re-bind via _refreshSSView)
        if (this._evZE && this.mainWrap) utils.off(this.mainWrap, "mouseenter", this._evZE);
        if (this._evZM && this.mainWrap) utils.off(this.mainWrap, "mousemove",  this._evZM);
        if (this._evZL && this.mainWrap) utils.off(this.mainWrap, "mouseleave", this._evZL);
        if (this._evTS && this.mainWrap) utils.off(this.mainWrap, "touchstart", this._evTS);
        if (this._evTM && this.mainWrap) utils.off(this.mainWrap, "touchmove",  this._evTM);
        if (this._evTE && this.mainWrap) utils.off(this.mainWrap, "touchend",   this._evTE);
        this._evZE = this._evZM = this._evZL = this._evTS = this._evTM = this._evTE = null;
        if (this._ssClickFns) {
            this._ssClickFns.forEach(({ el, fn }) => utils.off(el, "click", fn));
            this._ssClickFns = null;
        }
        if (this._evPgSS && this.pagination) {
            utils.off(this.pagination, "click", this._evPgSS);
            this._evPgSS = null;
        }
        if (this._evLbTrigger && this.lbTrigger) {
            utils.off(this.lbTrigger, "click", this._evLbTrigger);
            this._evLbTrigger = null;
        }
        if (this._evImgClick && this.mainWrap) {
            utils.off(this.mainWrap, "click", this._evImgClick);
            this._evImgClick = null;
            this.mainWrap.style.cursor = "";
        }
        if (this._evMainPrev && this.mainPrev) {
            utils.off(this.mainPrev, "click", this._evMainPrev);
            this._evMainPrev = null;
        }
        if (this._evMainNext && this.mainNext) {
            utils.off(this.mainNext, "click", this._evMainNext);
            this._evMainNext = null;
        }
        if (this._evSSVidClick && this.mainVideo) {
            utils.off(this.mainVideo, "click", this._evSSVidClick);
            this._evSSVidClick = null;
        }
        if (this._evSSImgLoad && this.mainImg) {
            utils.off(this.mainImg, "loadstart", this._evSSImgLoadStart);
            utils.off(this.mainImg, "load",      this._evSSImgLoad);
            this._evSSImgLoadStart = null;
            this._evSSImgLoad = null;
        }
        if (this._ssItemZoomEvents) {
            this._ssItemZoomEvents.forEach(({ el, event, fn }) =>
                utils.off(el, event, fn),
            );
            this._ssItemZoomEvents = null;
        }
        if (this._ssNoThumbRO) {
            this._ssNoThumbRO.disconnect();
            this._ssNoThumbRO = null;
        }
        this._destroyMainSwipe();
        this._destroyStackedSticky();
    },

    _scrollSSItemToView(index) {
        const el = this._ssItemEls && this._ssItemEls[index];
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    _syncSSNoThumbHeight() {
        if (!this._ssItemEls) return;
        const noThumbEls = this._ssItemEls.filter((el) =>
            el.classList.contains("wcpg-ss-no-thumb"),
        );
        if (!noThumbEls.length) return;

        // Find the nearest sibling that has a real image with known dimensions.
        // Walk the full list and prefer the first one that has already loaded.
        const refEl =
            this._ssItemEls.find((el) => {
                if (el.classList.contains("wcpg-ss-no-thumb")) return false;
                const img = el.querySelector(".wcpg-ss-img");
                return img && img.naturalWidth > 0;
            }) ||
            this._ssItemEls.find(
                (el) =>
                    !el.classList.contains("wcpg-ss-no-thumb") &&
                    el.querySelector(".wcpg-ss-img"),
            );

        const h = refEl ? refEl.offsetHeight : 0;
        if (h > 0) {
            noThumbEls.forEach((el) => {
                el.style.minHeight = h + "px";
            });
        }
        // h === 0 means images haven't loaded yet — keep the CSS fallback (200px)
        // until the ResizeObserver fires after the reference image loads.
    },

    _refreshSSView() {
        this._destroySSEvents();

        // Rebuild scroll items in-place
        if (this.ssScrollWrap) {
            this.ssScrollWrap.innerHTML = "";
            this._ssItemEls = [];
            this.items.forEach((item, i) => {
                const itemEl = this._buildSSScrollItem(item, i);
                if (i !== 0 || this.opts.stickySplitShowMainInScroll !== false) {
                    this.ssScrollWrap.appendChild(itemEl);
                }
                this._ssItemEls.push(itemEl);
            });
        }

        this.index = 0;
        this._renderItem(0, true);
        this._bindStickySplitEvents();
    },

    // ── Hierarchy Grid layout methods ─────────────────────────────────────────

    _bindHierarchyGridEvents() {
        this._evHGClicks = [];

        if (this.opts.lightbox) {
            // Expand-icon buttons on individual items
            if (this._hgLbTriggers && this._hgLbTriggers.length) {
                this._hgLbTriggers.forEach(({ el, index }) => {
                    const fn = (e) => {
                        e.stopPropagation();
                        this._lbOpen(index);
                    };
                    utils.on(el, "click", fn);
                    this._evHGClicks.push({ el, fn });
                });
            }

            // Click-to-open on image items (lightboxOpenOnImageClick)
            if (this.opts.lightboxOpenOnImageClick && this.hgItems) {
                this.hgItems.forEach((itemWrap, i) => {
                    const item = this.items[i];
                    if (!item || item.type !== "image") return;
                    const fn = (e) => {
                        if (e.target.closest(".wcpg-lb-trigger")) return;
                        this._lbOpen(i);
                    };
                    utils.on(itemWrap, "click", fn);
                    this._evHGClicks.push({ el: itemWrap, fn });
                    if (!this._isMobile) itemWrap.style.cursor = "zoom-in";
                });
            }
        }

        // Linked video play / close — reuse stacked linked video methods
        this._hgLvEvents = [];
        if (this.hgItems) {
            this.hgItems.forEach((itemWrap, i) => {
                const item = this.items[i];
                if (!item || !item.linkedVideoType) return;
                const playBtn = itemWrap.querySelector(".wcpg-linked-video-btn");
                if (playBtn) {
                    const fn = (e) => {
                        e.stopPropagation();
                        this._playStackedLinkedVideo(itemWrap, item);
                    };
                    utils.on(playBtn, "click", fn);
                    this._hgLvEvents.push({ el: playBtn, fn });
                }
                const closeBtn = itemWrap.querySelector(".wcpg-stacked-linked-close");
                if (closeBtn) {
                    const fn = (e) => {
                        e.stopPropagation();
                        this._stopStackedLinkedVideo(itemWrap, item);
                    };
                    utils.on(closeBtn, "click", fn);
                    this._hgLvEvents.push({ el: closeBtn, fn });
                }
            });
        }

        // Click-to-play/pause for hosted video items and linked hosted videos
        if (!this.opts.videoControls && this.hgItems) {
            this.hgItems.forEach((itemWrap, i) => {
                const item = this.items[i];
                if (!item) return;
                const vids = [];
                if (item.type === "video") {
                    const v = itemWrap.querySelector(".wcpg-hg-video");
                    if (v) vids.push(v);
                }
                if (
                    item.linkedVideoType &&
                    item.linkedVideoType !== "youtube" &&
                    item.linkedVideoType !== "vimeo"
                ) {
                    const v = itemWrap.querySelector(".wcpg-stacked-lv-video");
                    if (v) vids.push(v);
                }
                vids.forEach((vid) => {
                    const fn = (e) => {
                        e.stopPropagation();
                        if (vid.paused) vid.play().catch(() => {});
                        else vid.pause();
                    };
                    utils.on(vid, "click", fn);
                    this._hgLvEvents.push({ el: vid, fn });
                });
            });
        }

        // Per-item hover zoom
        if (
            this.opts.hierarchyGridZoom !== false &&
            this.opts.zoomOnHover &&
            !this._isMobile
        ) {
            this._hgZoomEvents = [];
            this.hgItems &&
                this.hgItems.forEach((itemWrap, i) => {
                    const item = this.items[i];
                    if (!item || item.type !== "image") return;
                    const img = itemWrap.querySelector(".wcpg-hg-img");
                    if (!img) return;
                    const zoomDisplay =
                        itemWrap.querySelector(".wcpg-hg-display") || itemWrap;

                    const enterFn = (e) => {
                        if (itemWrap.classList.contains("wcpg-stacked-lv-active"))
                            return;
                        this.mainImg = img;
                        this.mainWrap = itemWrap;
                        this.mainDisplay = zoomDisplay;
                        this.index = i;
                        if (this.lens) zoomDisplay.appendChild(this.lens);
                        if (this.winCursor) zoomDisplay.appendChild(this.winCursor);
                        this._zoomEnter(e);
                    };
                    const moveFn = (e) => this._zoomMove(e);
                    const leaveFn = () => {
                        this._zoomLeave();
                        setTimeout(() => {
                            if (!this.isZooming) {
                                this.mainImg = null;
                                this.mainWrap = null;
                                this.mainDisplay = null;
                            }
                        }, 300);
                    };
                    utils.on(itemWrap, "mouseenter", enterFn);
                    utils.on(itemWrap, "mousemove", moveFn);
                    utils.on(itemWrap, "mouseleave", leaveFn);
                    this._hgZoomEvents.push(
                        { el: itemWrap, event: "mouseenter", fn: enterFn },
                        { el: itemWrap, event: "mousemove", fn: moveFn },
                        { el: itemWrap, event: "mouseleave", fn: leaveFn },
                    );
                });
        }
        if (this.opts.stackedStickyEnabled) {
            this._initStackedSticky();
        }
    },

    _refreshHierarchyGridView() {
        // Remove per-item listeners before rebuilding
        if (this._evHGClicks) {
            this._evHGClicks.forEach(({ el, fn }) => utils.off(el, "click", fn));
            this._evHGClicks = null;
        }
        if (this._hgLvEvents) {
            this._hgLvEvents.forEach(({ el, fn }) => utils.off(el, "click", fn));
            this._hgLvEvents = null;
        }
        if (this._hgZoomEvents) {
            this._hgZoomEvents.forEach(({ el, event, fn }) =>
                utils.off(el, event, fn),
            );
            this._hgZoomEvents = null;
        }

        this.index = 0;
        this.hgItems = [];
        this._hgLbTriggers = [];
        this._zoomLeave();
        this.mainImg = null;
        this.mainWrap = null;
        this.mainDisplay = null;

        if (this.hgWrap) {
            this.hgWrap.innerHTML = "";
            if (this.items.length > 0) {
                const heroWrap = this._buildSingleHGItem(this.items[0], 0, true);
                this.hgWrap.appendChild(heroWrap);
                this.hgItems.push(heroWrap);
            }
            if (this.items.length > 1) {
                const grid = utils.el("div", "wcpg-hg-grid");
                for (let i = 1; i < this.items.length; i++) {
                    const itemWrap = this._buildSingleHGItem(this.items[i], i, false);
                    grid.appendChild(itemWrap);
                    this.hgItems.push(itemWrap);
                }
                this.hgWrap.appendChild(grid);
            }
        }

        this._bindHierarchyGridEvents();
    },

    _bindTilesEvents() {
        this._evTilesClicks = [];
        this._tilesZoomEvents = [];

        // Lightbox: expand-icon buttons
        if (this.opts.lightbox && this._tilesLbTriggers && this._tilesLbTriggers.length) {
            this._tilesLbTriggers.forEach(({ el, index }) => {
                const fn = (e) => {
                    e.stopPropagation();
                    this._lbOpen(index);
                };
                utils.on(el, "click", fn);
                this._evTilesClicks.push({ el, fn });
            });
        }

        // Click-to-open on image items
        if (this.opts.lightbox && this.opts.lightboxOpenOnImageClick && this.tilesItems) {
            this.tilesItems.forEach((itemWrap, i) => {
                const item = this.items[i];
                if (!item || item.type !== "image") return;
                const fn = (e) => {
                    if (e.target.closest(".wcpg-lb-trigger")) return;
                    this._lbOpen(i);
                };
                utils.on(itemWrap, "click", fn);
                this._evTilesClicks.push({ el: itemWrap, fn });
                if (!this._isMobile) itemWrap.style.cursor = "zoom-in";
            });
        }

        // Per-item hover zoom
        if (this.opts.tilesZoom !== false && this.opts.zoomOnHover && !this._isMobile && this.tilesItems) {
            this.tilesItems.forEach((itemWrap, i) => {
                const item = this.items[i];
                if (!item || item.type !== "image") return;
                const img = itemWrap.querySelector(".wcpg-tiles-img");
                if (!img) return;
                const zoomDisplay = itemWrap.querySelector(".wcpg-tiles-display") || itemWrap;

                const enterFn = (e) => {
                    this.mainImg = img;
                    this.mainWrap = itemWrap;
                    this.mainDisplay = zoomDisplay;
                    this.index = i;
                    if (this.lens) zoomDisplay.appendChild(this.lens);
                    if (this.winCursor) zoomDisplay.appendChild(this.winCursor);
                    this._zoomEnter(e);
                };
                const moveFn = (e) => this._zoomMove(e);
                const leaveFn = () => {
                    this._zoomLeave();
                    setTimeout(() => {
                        if (!this.isZooming) {
                            this.mainImg = null;
                            this.mainWrap = null;
                            this.mainDisplay = null;
                        }
                    }, 300);
                };
                utils.on(itemWrap, "mouseenter", enterFn);
                utils.on(itemWrap, "mousemove", moveFn);
                utils.on(itemWrap, "mouseleave", leaveFn);
                this._tilesZoomEvents.push(
                    { el: itemWrap, event: "mouseenter", fn: enterFn },
                    { el: itemWrap, event: "mousemove", fn: moveFn },
                    { el: itemWrap, event: "mouseleave", fn: leaveFn },
                );
            });
        }

        // Show More / Show Less button
        if (this._tilesShowMoreBtn) {
            this._evTilesShowMore = () => {
                this._tilesExpanded = !this._tilesExpanded;
                const initialCount = this.opts.tilesInitialCount > 0
                    ? this.opts.tilesInitialCount
                    : this.items.length;

                if (this._tilesExpanded) {
                    // Reveal all hidden tiles, then scroll to the first new one
                    const firstNewEl = this.tilesItems[initialCount] || null;
                    this.tilesItems.forEach((el, i) => {
                        if (i >= initialCount) el.classList.remove("wcpg-tiles-hidden");
                    });
                    if (firstNewEl) {
                        requestAnimationFrame(() => {
                            const rect = firstNewEl.getBoundingClientRect();
                            const scrollTop = window.scrollY + rect.top - 60;
                            window.scrollTo({ top: scrollTop, behavior: "smooth" });
                        });
                    }
                } else {
                    // Hide tiles beyond initialCount and scroll gallery back into view
                    this.tilesItems.forEach((el, i) => {
                        if (i >= initialCount) el.classList.add("wcpg-tiles-hidden");
                    });
                    // Scroll the gallery wrapper into view
                    if (this.wrapper) {
                        const rect = this.wrapper.getBoundingClientRect();
                        const scrollTop = window.scrollY + rect.top - 60;
                        window.scrollTo({ top: scrollTop, behavior: "smooth" });
                    }
                }

                this._updateTilesShowMoreBtn();
            };
            utils.on(this._tilesShowMoreBtn, "click", this._evTilesShowMore);
        }

        if (this.opts.stackedStickyEnabled) {
            this._initStackedSticky();
        }
    },

    _refreshTilesView() {
        // Clean up all tiles event listeners
        if (this._evTilesClicks) {
            this._evTilesClicks.forEach(({ el, fn }) => utils.off(el, "click", fn));
            this._evTilesClicks = null;
        }
        if (this._tilesZoomEvents) {
            this._tilesZoomEvents.forEach(({ el, event, fn }) => utils.off(el, event, fn));
            this._tilesZoomEvents = null;
        }
        if (this._evTilesShowMore && this._tilesShowMoreBtn) {
            utils.off(this._tilesShowMoreBtn, "click", this._evTilesShowMore);
            this._evTilesShowMore = null;
        }

        this.index = 0;
        this.tilesItems = [];
        this._tilesLbTriggers = [];
        this._tilesExpanded = false;
        this._zoomLeave();
        this.mainImg = null;
        this.mainWrap = null;
        this.mainDisplay = null;

        if (this.tilesGrid) {
            this.tilesGrid.innerHTML = "";
            const initialCount = this.opts.tilesInitialCount > 0
                ? this.opts.tilesInitialCount
                : this.items.length;

            this.items.forEach((item, i) => {
                const itemWrap = this._buildSingleTilesItem(item, i);
                if (i >= initialCount) itemWrap.classList.add("wcpg-tiles-hidden");
                this.tilesGrid.appendChild(itemWrap);
                this.tilesItems.push(itemWrap);
            });

            // Update show more button visibility
            const hasHidden = this.items.length > initialCount;
            if (this._tilesShowMoreWrap) {
                this._tilesShowMoreWrap.style.display = (this.opts.tilesShowMoreEnabled !== false && hasHidden) ? "" : "none";
            }
            this._updateTilesShowMoreBtn();
        }

        this._bindTilesEvents();
    },
};

export default galleryMethods;
